import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { CopyableLinkCell } from '../../components/ui/CopyableLinkCell';
import { PinBadge } from '../../components/ui/PinBadge';
import { StickyNav } from '../../components/ui/StickyNav';
import { DriveAssetsBrowser } from '../../components/drive/DriveAssetsBrowser';
import { ApiError } from '../../lib/api';
import { centsToDisplay } from '../../lib/money';
import {
  computeAccountCampaignStatus,
  statusPillClass,
  type RowForStatus,
} from '../../lib/accountCampaignStatus';
import { isBudgetModified } from '../../lib/dailyBudget';
import {
  effectiveGoogleAdLabelTemplate,
  effectiveMetaCampaignNameTemplate,
} from '../../lib/effectiveCampaignName';
import { effectiveMetaAdType, type EffectiveMetaAdType } from '../../lib/effectiveMetaAdType';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { resolveMonthYear } from '../../lib/monthYear';
import { effectiveRunsGoogle, effectiveRunsMeta } from '../../lib/platformEnabled';
import { renderTemplate } from '../../lib/templating';
import { slugifyPath } from '../../lib/rowContent';
import { getAccountDetail } from '../accounts/api';
import { getCampaign } from '../campaigns/api';
import {
  BodyCopyArrayEditor,
  DescriptionArrayEditor,
  GoogleAssetsEditor,
  HeadlineArrayEditor,
  LandingPageOverrideEditor,
  LeadFormEditor,
  PlaceholderHint,
  UtmConfigEditor,
} from '../campaigns/editors';
import { getFranchiseLocationProfile, getFranchiseProfile } from '../franchise/api';
import type {
  AssetLabel,
  Campaign,
  CampaignLeadForm,
  CtaOption,
  GoogleAssets,
  LandingPageOverride,
  LandingPageType,
  LeadFormText,
  MetaAdCopy,
  UtmConfig,
} from '../../types/campaign';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { FormType, FranchiseLocationProfile } from '../../types/franchiseLocationProfile';
import type { FranchiseProfile } from '../../types/franchiseProfile';
import { AuditTimeline } from './AuditTimeline';
import { BudgetDriftModal } from './BudgetDriftModal';
import { IncreaseInput, MonthlyBudgetInput } from './budgetInputs';
import { DailyBudgetCell } from './DailyBudgetCell';
import { DatePickerCell } from './DatePickerCell';
import { diffCampaignVsRow, type RowContentFieldKey } from './diffCampaignVsRow';
import { LeadFormCell } from './LeadFormCell';
import { resolveLeadForm } from '../../lib/resolveLeadForm';
import { hasEndDateDrift, hasStartDateDrift } from './hasRowModifications';
import {
  addRowLocalAssetLabelStubs,
  addRowOrganicAssetLabelStubs,
  getRollout,
  getRolloutPlanBacklink,
  getRow,
  listUsers,
  patchRow,
  patchRowBudget,
  patchRowContent,
  refreshRowFromCampaign,
  saveAsCampaign,
} from './api';
import {
  resolveKyckCampaignOverviewUrl,
  resolveOfferMechanicsUrl,
} from '../../lib/resolveOfferUrls';
import { SaveAsCampaignModal } from './SaveAsCampaignModal';
import { RefreshFromCampaignModal } from './RefreshFromCampaignModal';
import { resolveDisplayCampaignName } from './resolveDisplayCampaignName';
import { useRowAuditLog } from './useRowAuditLog';
import { SendPreviewModal, SendResultToast, type SendResult } from '../preview';

// Row editor entry point. Route: /rollouts/:rolloutId/rows/:rowId (P2b-2
// §7.4). Chunk B ships read mode + modification badges + resolved previews.
// Edit mode, Refresh from Campaign, and the Duplicate / History flows land
// in later chunks — the scaffolding here anticipates them so adding those
// pieces is contained.
//
// Layout mirrors CampaignDetail: sticky sidebar nav on the left, single
// scrolling column on the right. Sections are anchored so the sidebar links
// scroll-jump rather than swap state.

type ResolveContext = {
  account: { name: string; slug: string };
  locationProfile: FranchiseLocationProfile | null;
  franchiseProfile: FranchiseProfile | null;
};

// Resolved template preview. Falls back to an italic dash when the template
// is empty/null so the row editor always renders SOMETHING — empty string
// would be invisible. Unresolved placeholders stay literal (matches server-
// side rendering semantics); the author can see "{gymInfo}" in the preview
// if the location lacks a value.
function Resolved({
  template,
  ctx,
  className,
  placeholder = '—',
}: {
  template: string | null | undefined;
  ctx: ResolveContext;
  className?: string;
  placeholder?: string;
}) {
  if (!template) return <span className="text-slate-400">{placeholder}</span>;
  const { output } = renderTemplate(
    template,
    ctx.account,
    ctx.locationProfile,
    ctx.franchiseProfile,
  );
  return <span className={className}>{output}</span>;
}

// URL variant of Resolved: substitutes placeholders, then hands the
// resolved string to CopyableLinkCell so the buyer gets a compact
// "Open" affordance with hover-copy. Used for Offer Mechanics +
// Kyck Campaign Overview URLs in view mode (round 2 polish). Null /
// empty template falls through to CopyableLinkCell's em-dash.
function ResolvedLink({
  template,
  ctx,
  ariaLabel,
}: {
  template: string | null | undefined;
  ctx: ResolveContext;
  ariaLabel: string;
}) {
  const resolved = template
    ? renderTemplate(template, ctx.account, ctx.locationProfile, ctx.franchiseProfile)
        .output
    : null;
  return (
    <CopyableLinkCell
      href={resolved}
      display={<span>Open</span>}
      ariaLabel={ariaLabel}
    />
  );
}

// Consolidation §4: read-only display of a Plan-owned offer URL on the
// row editor. No per-row override anymore — the Plan is the single source
// (resolveOfferUrls). Null = orphan Rollout / unset on the Plan.
function PlanOfferUrl({
  url,
  ariaLabel,
}: {
  url: string | null;
  ariaLabel: string;
}) {
  if (!url) {
    return (
      <span className="text-sm text-slate-400">
        — <span className="text-xs">(set on the Plan)</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <CopyableLinkCell
        href={url}
        display={<span>Open</span>}
        ariaLabel={ariaLabel}
      />
      <span className="text-xs text-slate-400">from Plan</span>
    </span>
  );
}

function SectionShell({
  id,
  title,
  badge,
  children,
}: {
  id: string;
  title: string;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section id={id} className="bg-white rounded-md border border-slate-200 p-6 scroll-mt-4">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {badge}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  value,
  modified,
}: {
  label: string;
  value: ReactNode;
  modified?: boolean;
}) {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-4 py-1.5 items-start">
      <dt className="text-sm text-slate-500 flex items-center gap-1.5">
        <span>{label}</span>
        {modified && <ModifiedBadge />}
      </dt>
      <dd className="text-sm text-slate-900">
        {value ?? <span className="text-slate-400">—</span>}
      </dd>
    </div>
  );
}

// Tri-state platform override (Pre-P2b-3 §3.3). Three states packed into a
// select since a tri-state checkbox is more confusing than helpful for end
// users:
//
//   override === null  → "Inherit"  (rendered as "Inherit (Account runs Meta)"
//                                    or "Inherit (Account does not run Meta)")
//   override === true  → "Forced on"
//   override === false → "Forced off"
//
// Annotation underneath surfaces what the inherited state means so the AM
// can make an informed choice. Click-and-go: change fires the inline patch
// immediately.
function PlatformOverrideControl({
  platform,
  override,
  accountFlag,
  busy,
  disabled,
  onChange,
}: {
  platform: 'meta' | 'google';
  override: boolean | null | undefined;
  accountFlag: boolean;
  busy: boolean;
  disabled: boolean;
  onChange: (next: boolean | null) => void;
}) {
  const platformLabel = platform === 'meta' ? 'Meta' : 'Google';
  // override may be undefined on legacy row docs; treat as null for the
  // select's value so the control mounts cleanly.
  const value: 'inherit' | 'on' | 'off' =
    override === true ? 'on' : override === false ? 'off' : 'inherit';
  const inheritedHint = accountFlag
    ? `Account runs ${platformLabel}`
    : `Account does not run ${platformLabel}`;
  return (
    <div className="space-y-1">
      <select
        className="rounded-md border border-slate-300 px-2 py-1 text-sm disabled:opacity-50"
        value={value}
        disabled={disabled || busy}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next === 'on' ? true : next === 'off' ? false : null);
        }}
      >
        <option value="inherit">Inherit</option>
        <option value="on">Forced on</option>
        <option value="off">Forced off</option>
      </select>
      <p className="text-xs text-slate-500">({inheritedHint})</p>
    </div>
  );
}

// Pre-P2b-3 polish round 5 §1: per-row Meta ad type override widget.
// Two render modes driven by the Campaign-level metaAdType:
//
//   Campaign 'traffic' / 'lead_gen': read-only label showing the locked
//     value with annotation "(set on Campaign)". The row override and
//     location default are ignored — the helper effectiveMetaAdType
//     short-circuits before consulting them.
//
//   Campaign 'both': tri-state dropdown letting the AM pick this row's
//     ad type, or inherit from the location's defaultMetaAdType. Below
//     the dropdown, annotation explains the resolved inheritance:
//       - "(Inherits: Website Lead)" — location default is 'traffic'
//       - "(Inherits: Meta Lead Gen)" — location default is 'lead_gen'
//       - "(No location default — must pick)" — location default null
//
// Click-and-go: change fires the inline patch immediately.
function MetaAdTypeControl({
  campaignMetaAdType,
  rowOverride,
  locationDefault,
  busy,
  disabled,
  onChange,
}: {
  campaignMetaAdType: 'traffic' | 'lead_gen' | 'both';
  rowOverride: 'traffic' | 'lead_gen' | null | undefined;
  locationDefault: 'traffic' | 'lead_gen' | null | undefined;
  busy: boolean;
  disabled: boolean;
  onChange: (next: 'traffic' | 'lead_gen' | null) => void;
}) {
  if (campaignMetaAdType !== 'both') {
    const label = campaignMetaAdType === 'traffic' ? 'Website Lead' : 'Meta Lead Gen';
    return (
      <div className="space-y-1">
        <span className="text-sm text-slate-900">{label}</span>
        <p className="text-xs text-slate-500 italic">(set on Campaign)</p>
      </div>
    );
  }
  // Campaign is 'both' — tri-state dropdown.
  const value: 'inherit' | 'traffic' | 'lead_gen' =
    rowOverride === 'traffic' ? 'traffic' : rowOverride === 'lead_gen' ? 'lead_gen' : 'inherit';
  const inheritedHint =
    locationDefault === 'traffic'
      ? '(Inherits: Website Lead)'
      : locationDefault === 'lead_gen'
        ? '(Inherits: Meta Lead Gen)'
        : '(No location default — must pick)';
  return (
    <div className="space-y-1">
      <select
        className="rounded-md border border-slate-300 px-2 py-1 text-sm disabled:opacity-50"
        value={value}
        disabled={disabled || busy}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next === 'traffic' ? 'traffic' : next === 'lead_gen' ? 'lead_gen' : null);
        }}
      >
        <option value="inherit">Inherit from location default</option>
        <option value="traffic">Website Lead</option>
        <option value="lead_gen">Meta Lead Gen</option>
      </select>
      {value === 'inherit' && (
        <p className="text-xs text-slate-500">{inheritedHint}</p>
      )}
    </div>
  );
}

// Per-row Campaign name override editor (Pre-P2b-3 §4). Read mode shows
// the resolved name (override > Campaign.name > "— No Campaign —") with
// a small italic indicator when the override is set. Edit mode swaps in
// a text input with the underlying Campaign name as placeholder; save-
// on-blur via the inline patch mutation. Empty input → null on the
// wire (override cleared, falls back to Campaign.name).
function CampaignNameField({
  row,
  campaign,
  archived,
  busy,
  editing,
  onCommit,
}: {
  row: import('../../types/accountCampaign').AccountCampaign;
  campaign: import('../../types/campaign').Campaign | null;
  archived: boolean;
  busy: boolean;
  editing: boolean;
  onCommit: (next: string | null) => void;
}) {
  const resolved = resolveDisplayCampaignName(row, campaign);
  if (!editing) {
    return (
      <span>
        {resolved}
        {row.campaignNameOverride && campaign && (
          <span
            className="ml-2 text-xs italic text-amber-700"
            title={`Underlying Campaign: ${campaign.name}`}
          >
            (custom name)
          </span>
        )}
      </span>
    );
  }
  return (
    <div className="space-y-1">
      <input
        type="text"
        // Force a remount when the underlying override changes (Save
        // Alternate / opt-out / reassignment) so defaultValue tracks
        // the latest server state.
        key={`name-override-${row.id}-${row.campaignNameOverride ?? 'null'}`}
        defaultValue={row.campaignNameOverride ?? ''}
        disabled={archived || busy}
        placeholder={campaign?.name ?? '— No Campaign —'}
        maxLength={200}
        onBlur={(e) => {
          const trimmed = e.target.value.trim();
          const next = trimmed === '' ? null : trimmed;
          if (next === (row.campaignNameOverride ?? null)) return;
          onCommit(next);
        }}
        className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm disabled:opacity-60"
      />
      <p className="text-xs text-slate-500">
        Leave blank to display the underlying Campaign name.
      </p>
    </div>
  );
}

// Row notes editor in the row editor's Overview section (Pre-P2b-3
// polish Fix 2). Inline auto-grow textarea — no popover, no draft
// state, click-and-go save-on-blur. Master grid keeps its NotesCell
// popover for the compact column; the row editor has the space for an
// inline field.
//
// Auto-grow: rows={6} fixed with native scroll. The textarea's
// height clamps to 6 rows so the Overview dl stays scannable; the
// scrollbar appears for longer notes. Native CSS field-sizing: content
// would be lighter but isn't broadly supported yet — vanilla rows + a
// resize handle is reliable.
function RowNotesField({
  row,
  archived,
  busy,
  onCommit,
}: {
  row: import('../../types/accountCampaign').AccountCampaign;
  archived: boolean;
  busy: boolean;
  onCommit: (next: string | null) => void;
}) {
  if (archived) {
    return row.rowNotes ? (
      <p className="whitespace-pre-wrap text-slate-700">{row.rowNotes}</p>
    ) : (
      <span className="text-slate-400 italic">(none)</span>
    );
  }
  return (
    <textarea
      // Force a remount on row change so defaultValue tracks the
      // latest server state without clobbering an in-progress edit.
      key={`row-notes-${row.id}-${row.rowNotes ?? 'null'}`}
      defaultValue={row.rowNotes ?? ''}
      disabled={busy}
      placeholder="Any notes for media buyers, the client, or future-you…"
      rows={6}
      onBlur={(e) => {
        const trimmed = e.target.value.trim();
        const next = trimmed === '' ? null : trimmed;
        if (next === (row.rowNotes ?? null)) return;
        onCommit(next);
      }}
      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60 resize-y"
    />
  );
}

// Local assets URL editor in the row editor's Assets section (Pre-P2b-3
// polish Fix 2). Single-line URL input — same click-and-go pattern as
// the other folder URL fields. Read-only display under the input shows
// the existing URL as a clickable link so the AM can verify the value
// without opening another tab.
function LocalAssetsField({
  row,
  archived,
  busy,
  onCommit,
}: {
  row: import('../../types/accountCampaign').AccountCampaign;
  archived: boolean;
  busy: boolean;
  onCommit: (next: string | null) => void;
}) {
  if (archived) {
    return row.localAssetsUrl ? (
      <a
        href={row.localAssetsUrl}
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 hover:underline break-all text-sm"
      >
        {row.localAssetsUrl}
      </a>
    ) : (
      <span className="text-slate-400 italic">(none)</span>
    );
  }
  return (
    <div className="space-y-1">
      <input
        type="text"
        key={`local-assets-${row.id}-${row.localAssetsUrl ?? 'null'}`}
        defaultValue={row.localAssetsUrl ?? ''}
        disabled={busy}
        placeholder="https://drive.google.com/drive/folders/..."
        onBlur={(e) => {
          const trimmed = e.target.value.trim();
          const next = trimmed === '' ? null : trimmed;
          if (next === (row.localAssetsUrl ?? null)) return;
          onCommit(next);
        }}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
      />
      {row.localAssetsUrl && (
        <a
          href={row.localAssetsUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-blue-600 hover:underline break-all"
        >
          Open →
        </a>
      )}
    </div>
  );
}

// OrganicAssets §9.3. Organic mirror of LocalAssetsField — same click-and-go
// URL editor against row.organicAssetsUrl.
function OrganicAssetsField({
  row,
  archived,
  busy,
  onCommit,
}: {
  row: import('../../types/accountCampaign').AccountCampaign;
  archived: boolean;
  busy: boolean;
  onCommit: (next: string | null) => void;
}) {
  if (archived) {
    return row.organicAssetsUrl ? (
      <a
        href={row.organicAssetsUrl}
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 hover:underline break-all text-sm"
      >
        {row.organicAssetsUrl}
      </a>
    ) : (
      <span className="text-slate-400 italic">(none)</span>
    );
  }
  return (
    <div className="space-y-1">
      <input
        type="text"
        key={`organic-assets-${row.id}-${row.organicAssetsUrl ?? 'null'}`}
        defaultValue={row.organicAssetsUrl ?? ''}
        disabled={busy}
        placeholder="https://drive.google.com/drive/folders/..."
        onBlur={(e) => {
          const trimmed = e.target.value.trim();
          const next = trimmed === '' ? null : trimmed;
          if (next === (row.organicAssetsUrl ?? null)) return;
          onCommit(next);
        }}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
      />
      {row.organicAssetsUrl && (
        <a
          href={row.organicAssetsUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-blue-600 hover:underline break-all"
        >
          Open →
        </a>
      )}
    </div>
  );
}

function ModifiedBadge() {
  // Small amber dot next to modified fields (§7.4.5). Tooltip on hover.
  // The badge semantics: "differs from Campaign's current value" covers both
  // AM overrides (row-authored) and Campaign-level drift (brand team edited
  // the Campaign after the row snapshotted). Both cases look the same.
  return (
    <span
      className="inline-block w-2 h-2 rounded-full bg-amber-500"
      aria-label="Custom value — differs from Campaign's current value"
      title="Custom value — differs from Campaign's current value"
    />
  );
}

// Small "X / Y" array preview. Render as an ordered list so reorder is
// visible. `templateFormatter` lets the caller run each entry through
// renderTemplate for Meta / Google content; the raw strings are used for
// length-sensitive previews (paths, URLs).
function StringList({
  items,
  pins,
  ctx,
  templated,
  emptyLabel = '(none)',
}: {
  items: string[] | null | undefined;
  // Pinned Headlines spec §2.3: optional per-item pin positions,
  // parallel index to `items`. Only the Google headlines field passes
  // this; other call sites omit it and no badge renders.
  pins?: Array<1 | 2 | 3 | null> | null;
  ctx: ResolveContext;
  templated: boolean;
  emptyLabel?: string;
}) {
  if (!items || items.length === 0) {
    return <span className="text-slate-400 italic">{emptyLabel}</span>;
  }
  return (
    <ol className="space-y-1 list-decimal list-inside">
      {items.map((v, i) => {
        const pin = pins ? pins[i] ?? null : null;
        return (
          <li key={i} className="text-sm text-slate-800 whitespace-pre-wrap">
            <span className="inline-flex items-center gap-1.5 align-middle">
              {templated ? <Resolved template={v} ctx={ctx} /> : v}
              <PinBadge pinPosition={pin} />
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function leadFormTextDisplay(
  value: LeadFormText,
  ctx: ResolveContext,
): ReactNode {
  if (value.mode === 'Generic') return <span>Generic</span>;
  return (
    <span>
      Custom — <Resolved template={value.text} ctx={ctx} />
    </span>
  );
}

// Multi Lead Form spec §3.3 + §3.4. Inline banner above the row editor's
// Lead form block. Orphan rows surface a red warning + dropdown; multi-
// form OK rows surface a muted info bar + dropdown so the AM can switch
// without scrolling back to the master grid. Single-form rows render
// nothing (no surface). Archived rollouts suppress the control surface
// entirely.
function LeadFormReassignBanner({
  row,
  campaign,
  archived,
  busy,
  onReassign,
}: {
  row: AccountCampaign;
  campaign: Campaign | null;
  archived: boolean;
  busy: boolean;
  onReassign: (leadFormId: string) => void;
}) {
  if (!campaign) return null;
  const resolved = resolveLeadForm(row, campaign);
  const multiForm = (campaign.campaignLeadForms?.length ?? 0) > 1;
  const orphan = resolved.state === 'orphan';
  if (archived || (!multiForm && !orphan)) return null;
  if (orphan) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
        <span className="flex-1">
          ⚠ This row's lead form was deleted from the Campaign. The fields
          below still display the row's snapshot. Reassign to clear the
          warning.
        </span>
        <LeadFormCell
          row={row}
          campaign={campaign}
          disabled={false}
          busy={busy}
          onChange={onReassign}
          ariaLabel="Reassign lead form"
        />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
      <span>
        This Campaign has {campaign.campaignLeadForms.length} lead forms.
      </span>
      <LeadFormCell
        row={row}
        campaign={campaign}
        disabled={false}
        busy={busy}
        onChange={onReassign}
        ariaLabel="Reassign lead form"
      />
    </div>
  );
}

function landingOverrideDisplay(
  override: LandingPageOverride | null,
): ReactNode {
  if (!override) return <span className="text-slate-400">—</span>;
  if (override.type === 'inherit') return <span>Inherits from Overview</span>;
  if (override.type === 'custom') {
    return (
      <span>
        Custom — <code className="font-mono text-xs break-all">{override.customUrl}</code>
      </span>
    );
  }
  return <span>Brand {override.type} template</span>;
}

// Edit-mode draft shape. Mirrors the row's snapshotted content fields —
// exactly the set the /content PATCH endpoint accepts. Row-authored fields
// (budget, dates, AM, ready, execution, notes, localAssetsUrl) are NOT in
// this shape; those go through the inline-patch flow on the master grid,
// not the row editor's Save button.
//
// URL fields are stored as '' in the draft for ergonomics (inputs bind to
// strings); empty string maps back to null when we build the PATCH.
//
// Consolidation §4: offerMechanicsUrl / kyckCampaignOverviewUrl are NO
// longer per-row editable — the Plan owns them, resolved live for display
// only (resolveOfferUrls). Dropped from the draft + PATCH entirely.
type EditState = {
  landingPageType: LandingPageType;
  customLandingPageUrl: string;
  metaAdCopy: MetaAdCopy;
  googleAssets: GoogleAssets;
  metaLandingPageOverride: LandingPageOverride;
  googleLandingPageOverride: LandingPageOverride;
  metaAssetsFolderUrl: string;
  googleAssetsFolderUrl: string;
  otherAssetsFolderUrl: string;
  utmConfig: UtmConfig;
  // Pre-P2b-3 polish round 2 (Item 1): Meta naming snapshot pair,
  // row-editable. Tri-state on the draft to preserve the storage
  // distinction:
  //   - null  = "inherit" — fall back to Campaign's value (legacy
  //             behaviour or AM cleared the override). Maps to null
  //             on save (and to undefined-passthrough on the wire
  //             when the row has been freshly seeded with null).
  //   - ''    = explicit empty — substitutes literally in the preview
  //             (per Q4: AM's choice if they want awkward spaces).
  //   - other = explicit value.
  // The "Clear override" affordance flips an explicit value back to
  // null. Empty-string is reachable via typing then deleting; the
  // input value renders '' for both null and '' visually so the AM
  // sees Campaign's value as placeholder text in inherit mode.
  campaignDescriptor: string | null;
  metaCampaignNameTemplate: string | null;
  // Pre-P2b-3 polish round 5 §2.2 + §2.5: Google ad-label template,
  // row-editable. Same tri-state semantics as metaCampaignNameTemplate.
  googleAdLabelTemplate: string | null;
  // Multi Lead Form spec §1.2 + §5. The row's frozen snapshot of a single
  // CampaignLeadForm. Edit-mode authors name / formType / fields here;
  // saving sends the full CampaignLeadForm to patchRowContent. Switching
  // WHICH lead form the row tracks goes through the master grid (inline
  // patch leadFormId), not this draft — that flow re-snapshots from the
  // Campaign and lands a fresh row state.
  leadForm: CampaignLeadForm | null;
};

const EMPTY_UTM_CONFIG: UtmConfig = {
  enabled: false,
  platforms: [],
  shared: { campaignId: null, campaignName: null },
  meta: null,
  google: null,
};

const EMPTY_META: MetaAdCopy = {
  metaAdType: 'lead_gen',
  bodyCopy: [],
  headlines: [],
  descriptions: [],
  ctaButton: 'Learn More',
};

const EMPTY_GOOGLE: GoogleAssets = {
  headlines: [],
  descriptions: [],
  path1: '',
  path2: null,
};

const CTA_OPTIONS: CtaOption[] = [
  'Get In Touch',
  'Get Offer',
  'Get Quote',
  'Learn More',
  'Sign Up',
  'Book Now',
  'Subscribe',
  'Apply Now',
  'Download',
];

const LANDING_TYPES: LandingPageType[] = ['offer', 'sale', 'location', 'custom'];
const FORM_TYPES: FormType[] = ['Generic', 'Qualifying Question'];

function initialEditState(row: AccountCampaign): EditState {
  return {
    landingPageType: row.landingPageType ?? 'offer',
    customLandingPageUrl: row.customLandingPageUrl ?? '',
    metaAdCopy: row.metaAdCopy ?? EMPTY_META,
    googleAssets: row.googleAssets ?? EMPTY_GOOGLE,
    metaLandingPageOverride: row.metaLandingPageOverride ?? { type: 'inherit' },
    googleLandingPageOverride: row.googleLandingPageOverride ?? { type: 'inherit' },
    metaAssetsFolderUrl: row.metaAssetsFolderUrl ?? '',
    googleAssetsFolderUrl: row.googleAssetsFolderUrl ?? '',
    otherAssetsFolderUrl: row.otherAssetsFolderUrl ?? '',
    utmConfig: row.utmConfig ?? EMPTY_UTM_CONFIG,
    // Tri-state initialisation:
    //   row field undefined (legacy)        → null (inherit)
    //   row field === null  (explicit none) → null (inherit)
    //   row field === ''    (explicit empty) → '' (explicit empty)
    //   row field === 'foo' (explicit value) → 'foo'
    // Treating null/undefined identically here is intentional — both
    // read as "no row override" in the UI; the storage distinction
    // matters for the diff helper, not for the draft.
    campaignDescriptor: row.campaignDescriptor ?? null,
    metaCampaignNameTemplate: row.metaCampaignNameTemplate ?? null,
    googleAdLabelTemplate: row.googleAdLabelTemplate ?? null,
    leadForm: row.leadForm ?? null,
  };
}

function strOrNull(s: string): string | null {
  const t = s.trim();
  return t.length === 0 ? null : t;
}

// Build the sparse PATCH body. Only include keys whose value actually
// changed compared to the saved row. Keeps the write payload small and
// makes the audit diff tight — an unchanged field isn't re-written.
function buildContentPatch(
  draft: EditState,
  row: AccountCampaign,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  if (draft.landingPageType !== row.landingPageType) {
    patch.landingPageType = draft.landingPageType;
  }

  // customLandingPageUrl: server enforces null when type != 'custom'. Respect
  // that in the patch: when user flipped away from custom, explicitly null it
  // out so the cross-field rule doesn't reject.
  const nextCustomUrl =
    draft.landingPageType === 'custom' ? strOrNull(draft.customLandingPageUrl) : null;
  if (nextCustomUrl !== row.customLandingPageUrl) patch.customLandingPageUrl = nextCustomUrl;

  if (JSON.stringify(draft.metaAdCopy) !== JSON.stringify(row.metaAdCopy)) {
    patch.metaAdCopy = draft.metaAdCopy;
  }
  if (JSON.stringify(draft.googleAssets) !== JSON.stringify(row.googleAssets)) {
    patch.googleAssets = draft.googleAssets;
  }
  if (
    JSON.stringify(draft.metaLandingPageOverride) !==
    JSON.stringify(row.metaLandingPageOverride)
  ) {
    patch.metaLandingPageOverride = draft.metaLandingPageOverride;
  }
  if (
    JSON.stringify(draft.googleLandingPageOverride) !==
    JSON.stringify(row.googleLandingPageOverride)
  ) {
    patch.googleLandingPageOverride = draft.googleLandingPageOverride;
  }
  if (JSON.stringify(draft.utmConfig) !== JSON.stringify(row.utmConfig)) {
    patch.utmConfig = draft.utmConfig;
  }

  const nextMetaFolder = strOrNull(draft.metaAssetsFolderUrl);
  if (nextMetaFolder !== row.metaAssetsFolderUrl) patch.metaAssetsFolderUrl = nextMetaFolder;
  const nextGoogleFolder = strOrNull(draft.googleAssetsFolderUrl);
  if (nextGoogleFolder !== row.googleAssetsFolderUrl)
    patch.googleAssetsFolderUrl = nextGoogleFolder;
  const nextOtherFolder = strOrNull(draft.otherAssetsFolderUrl);
  if (nextOtherFolder !== row.otherAssetsFolderUrl) patch.otherAssetsFolderUrl = nextOtherFolder;

  // Pre-P2b-3 polish round 2 (Item 1): Meta naming pair. Compare
  // tri-state to tri-state with the inherit-on-undefined semantics
  // pinned: row=undefined and draft=null are EQUIVALENT (both mean
  // "no row override"), so don't write null when the row has never
  // had a value — that would land an explicit null in storage and
  // surrender the legacy inherit behaviour. Only write when:
  //   - draft is non-null AND differs from row's value, OR
  //   - draft is null AND row had a non-undefined value (explicit
  //     clear).
  const rowDescRaw = row.campaignDescriptor; // string | null | undefined
  const draftDesc = draft.campaignDescriptor; // string | null
  if (draftDesc === null) {
    if (rowDescRaw !== undefined && rowDescRaw !== null) {
      patch.campaignDescriptor = null;
    }
  } else if (draftDesc !== rowDescRaw) {
    patch.campaignDescriptor = draftDesc;
  }

  const rowTplRaw = row.metaCampaignNameTemplate;
  const draftTpl = draft.metaCampaignNameTemplate;
  if (draftTpl === null) {
    if (rowTplRaw !== undefined && rowTplRaw !== null) {
      patch.metaCampaignNameTemplate = null;
    }
  } else if (draftTpl !== rowTplRaw) {
    patch.metaCampaignNameTemplate = draftTpl;
  }

  // Pre-P2b-3 polish round 5 §2.2 + §2.5: Google ad-label template,
  // same tri-state semantics as the Meta template above.
  const rowGoogleTpl = row.googleAdLabelTemplate;
  const draftGoogleTpl = draft.googleAdLabelTemplate;
  if (draftGoogleTpl === null) {
    if (rowGoogleTpl !== undefined && rowGoogleTpl !== null) {
      patch.googleAdLabelTemplate = null;
    }
  } else if (draftGoogleTpl !== rowGoogleTpl) {
    patch.googleAdLabelTemplate = draftGoogleTpl;
  }

  // Multi Lead Form spec §1.2 + §5. The row's frozen snapshot. Compare
  // structurally; include in the patch only when the AM actually edited
  // the snapshot. Switching WHICH lead form the row uses is the master
  // grid's job (inline patch leadFormId), not this draft.
  if (JSON.stringify(draft.leadForm) !== JSON.stringify(row.leadForm)) {
    patch.leadForm = draft.leadForm;
  }

  return patch;
}

// Section nav anchors. Prefix `re-` (Row Editor) keeps the ids
// distinct from BuildView's bv-* and CampaignForm's cf-*. Each id is
// the DOM id on a SectionShell rendered below; the StickyNav scroll-
// spy and click handlers anchor here.
const SECTION_NAV: Array<{ id: string; label: string }> = [
  { id: 're-overview', label: 'Overview' },
  { id: 're-budget', label: 'Budget' },
  { id: 're-meta-content', label: 'Meta content' },
  { id: 're-google-content', label: 'Google content' },
  { id: 're-utm', label: 'UTM config' },
  { id: 're-landing', label: 'Landing pages' },
  { id: 're-meta-execution', label: 'Meta execution' },
  { id: 're-google-execution', label: 'Google execution' },
  { id: 're-assets', label: 'Assets' },
  { id: 're-history', label: 'History' },
];

export function RowEditor() {
  const { id: rolloutId, rowId } = useParams<{ id: string; rowId: string }>();
  const qc = useQueryClient();

  // Row first; everything else depends on what the row tells us.
  const rowQuery = useQuery({
    queryKey: ['rollout', rolloutId, 'row', rowId],
    queryFn: () => getRow(rolloutId!, rowId!),
    enabled: !!rolloutId && !!rowId,
  });

  const row = rowQuery.data ?? null;

  // Mode + draft state. Draft is populated from the row when the user flips
  // into edit mode; kept as `null` otherwise so read-mode renders don't pay
  // for the copy. Re-initialised on every enter because the row may have
  // changed since the last edit (a refresh persisted mid-session, say).
  //
  // Navigating away via the back arrow (route change) silently discards the
  // draft — no beforeunload prompt, no confirmation. Sidebar navigation
  // within the page stays on the same route, so the draft persists across
  // in-page scrolls.
  const [mode, setMode] = useState<'read' | 'edit'>('read');
  const [draft, setDraft] = useState<EditState | null>(null);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsError, setSaveAsError] = useState<string | null>(null);
  // Ad Preview Stage 3 §3.1: single-send modal + result toast.
  const [sendOpen, setSendOpen] = useState(false);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  // Pre-P2b-3 polish (Fix 1): post-save toast covering both branches
  // — Campaign activated cleanly OR saved as draft with N missing
  // fields. Auto-dismisses after a short timeout; user can also click
  // through to the new Campaign.
  const [saveAsToast, setSaveAsToast] = useState<null | {
    campaignId: string;
    activated: boolean;
    warnings: Array<{ path: string; message: string }>;
  }>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Budget-drift modal state (Fix 4). Local to this surface; the master
  // grid keeps its own. Captured at blur time so the modal shows the
  // right values even if the row refetches while it's open.
  const [drift, setDrift] = useState<null | {
    platform: 'meta' | 'google';
    fromCents: number | null;
    toCents: number | null;
  }>(null);

  const enterEdit = () => {
    if (!row) return;
    setDraft(initialEditState(row));
    setSaveError(null);
    setMode('edit');
  };
  const cancelEdit = () => {
    setDraft(null);
    setSaveError(null);
    setMode('read');
  };

  // If the row refetches after a successful save/refresh, keep the draft in
  // sync with the latest saved shape so modification badges and the refresh
  // count stay accurate while the user continues editing.
  useEffect(() => {
    if (mode !== 'edit' || !row) return;
    // Only rebuild when switching into edit mode OR when a server-side
    // refresh just happened (distinguished by an empty draft). A naive
    // "always rebuild" would blow away every keystroke.
    if (draft === null) setDraft(initialEditState(row));
  }, [row, mode, draft]);

  const rolloutQuery = useQuery({
    queryKey: ['rollout', rolloutId, 'detail-only'],
    queryFn: () => getRollout(rolloutId!),
    enabled: !!rolloutId,
  });

  // Consolidation §4.2: offer URLs resolve live from the Rollout's parent
  // Plan (via promotionPlanId). Orphan Rollouts have no parent Plan → the
  // resolvers return null and the fields render as read-only "—".
  const planBacklinkId = rolloutQuery.data?.promotionPlanId ?? null;
  const planQuery = useQuery({
    queryKey: ['rollout-plan-backlink', planBacklinkId],
    queryFn: () => getRolloutPlanBacklink(planBacklinkId!),
    enabled: !!planBacklinkId,
  });
  const planOfferMechanicsUrl = resolveOfferMechanicsUrl(planQuery.data);
  const planKyckCampaignOverviewUrl = resolveKyckCampaignOverviewUrl(
    planQuery.data,
  );

  const accountQuery = useQuery({
    queryKey: ['account', row?.accountId],
    queryFn: () => getAccountDetail(row!.accountId),
    enabled: !!row?.accountId,
  });

  const locationProfileQuery = useQuery({
    queryKey: ['franchiseLocationProfile', row?.accountId],
    queryFn: () => getFranchiseLocationProfile(row!.accountId),
    enabled: !!row?.accountId,
  });

  // Brand comes via the rollout (brandAccountId). franchiseProfile gives us
  // defaultGymInfo for the {gymInfo} fallback in templating.
  const brandId = rolloutQuery.data?.brandAccountId ?? null;
  const franchiseProfileQuery = useQuery({
    queryKey: ['franchiseProfile', brandId],
    queryFn: () => getFranchiseProfile(brandId!),
    enabled: !!brandId,
  });

  // Campaign used both for modification-badge diffing and for the Refresh
  // from Campaign flow in chunk B commit 5. Only fetched when the row has a
  // campaignId (unassigned + opt-out rows skip this).
  const campaignQuery = useQuery({
    queryKey: ['campaign', row?.campaignId],
    queryFn: () => getCampaign(row!.campaignId!),
    enabled: !!row?.campaignId,
  });

  const usersQuery = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => listUsers(),
  });

  // Audit timeline for the History section. Hook lives next to the
  // component; RowEditor just composes the pair. Disabled until the
  // route params resolve to avoid a spurious first request.
  const audit = useRowAuditLog(rolloutId ?? null, rowId ?? null);

  const diffSet: Set<RowContentFieldKey> = useMemo(() => {
    if (!row || !campaignQuery.data) return new Set();
    return diffCampaignVsRow(row, campaignQuery.data);
  }, [row, campaignQuery.data]);

  // Pre-P2b-3 polish round 3 (Fix 2): defensive guard against the
  // race condition where an AM types a customisation in the row
  // editor's draft, then clicks "Save Campaign for reuse" before
  // clicking Save. Without this guard, saveAs reads the row's
  // server-side state, which still has the pre-edit values, and
  // the new Campaign drops whatever the AM just typed.
  //
  // Compute whether the draft has any unsaved diffs vs the saved
  // row. When non-empty, the saveAs button is disabled with a
  // tooltip telling the AM to save first. Cheap to recompute on
  // each keystroke — buildContentPatch is just key-by-key compares.
  //
  // Hook lives up here in the unconditional block (NOT next to the
  // saveAs button render) because the early returns below this point
  // for `rowQuery.isLoading` etc. would otherwise change the hook
  // count between renders and trip React error #310 ("Rendered more
  // hooks than during the previous render"). The hook guards on its
  // dependencies internally — no `editing` derived var yet, since
  // that's defined post-early-returns; we inline the same check
  // (mode === 'edit' && draft !== null) here.
  //
  // TODO (Pre-P2b-3 polish round 3): the cleaner fix is to auto-
  // save the draft as part of the saveAs flow (one transaction:
  // patch row content → create Campaign → repoint row). Skipped
  // here in favour of the simpler disable-button guard. Revisit
  // if AMs hit the "Save first" message often enough to be a
  // friction point.
  const draftHasUnsavedChanges = useMemo(() => {
    if (mode !== 'edit' || draft === null || !row) return false;
    const patch = buildContentPatch(draft, row);
    return Object.keys(patch).length > 0;
  }, [mode, draft, row]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!rolloutId || !rowId || !row || !draft) throw new Error('Missing state');
      const patch = buildContentPatch(draft, row);
      if (Object.keys(patch).length === 0) {
        // No changes — treat as a successful save so the user returns to
        // read mode instead of staring at a stuck Save button.
        return row;
      }
      return patchRowContent(rolloutId, rowId, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'row', rowId] });
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
      setDraft(null);
      setSaveError(null);
      setMode('read');
    },
    onError: (err: Error) => {
      setSaveError(err instanceof ApiError ? err.message : err.message);
    },
  });

  // Generic inline-patch mutation for row-authored fields. Click-and-go
  // semantics, parallel to the master grid's ready-toggle inline patch:
  // these aren't authored content (so not part of the draft state
  // machine), they're row-level config that fires immediately on
  // change. Used by:
  //   - Platform overrides (Pre-P2b-3 §3): metaEnabled / googleEnabled.
  //   - Campaign name override (Pre-P2b-3 §4): campaignNameOverride.
  //   - Notes + Local Assets (Pre-P2b-3 polish Fix 2): rowNotes /
  //     localAssetsUrl, edited inline in Overview / Assets sections.
  // Originally named platformOverrideMut; widened post-Session-B-polish
  // to the generic shape since the same wire mutation serves every
  // row-authored field.
  const inlinePatchMut = useMutation({
    mutationFn: async (patch: {
      metaEnabled?: boolean | null;
      googleEnabled?: boolean | null;
      campaignNameOverride?: string | null;
      rowNotes?: string | null;
      localAssetsUrl?: string | null;
      // Pre-P2b-3 polish round 3 (Fix 3): startDate / endDate fire
      // through the same inline-patch endpoint the master grid's
      // DatePickerCell uses. Server backfills *Original on legacy
      // rows; here we just hand the ISO string through.
      startDate?: string | null;
      endDate?: string | null;
      // Pre-P2b-3 polish round 5 §1: per-row Meta ad type override.
      // Click-and-go from the MetaAdTypeControl widget. 'both' is
      // not a valid row-level value (server zod also rejects).
      metaAdType?: 'traffic' | 'lead_gen' | null;
      // Multi Lead Form spec §3.4. The row editor's Lead form section
      // surfaces a reassign affordance when the assigned Campaign has
      // multiple forms (or the row is orphan). The patch carries the
      // target id; server re-snapshots row.leadForm from the Campaign.
      leadFormId?: string | null;
      // P2c Local Assets: row-authored Drive labels. Saved through the
      // same inline-patch endpoint as localAssetsUrl, but via a
      // debounced helper below so a quick edit settles into one
      // server write.
      localAssetLabels?: AssetLabel[];
      // OrganicAssets §9.3: Organic mirror of localAssetsUrl /
      // localAssetLabels.
      organicAssetsUrl?: string | null;
      organicAssetLabels?: AssetLabel[];
    }) => {
      if (!rolloutId || !rowId) throw new Error('Missing rollout or row id');
      return patchRow(rolloutId, rowId, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'row', rowId] });
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
    },
    onError: (err: Error) => {
      setSaveError(err instanceof ApiError ? err.message : err.message);
    },
  });

  // P2c Local Assets edit-mode wiring. The DriveAssetsBrowser is a
  // controlled component — every keystroke fires onChange with the next
  // full label array. Saving on every keystroke would mean one HTTP
  // request + one audit entry per character, so we debounce. Drafts
  // hold the in-flight value so the input stays responsive while the
  // server catches up.
  //
  // Sync rule: when the row refetches and there is NO pending save in
  // flight, clear the draft so future edits start from server state.
  // While a save is pending, leave the draft alone — the user might be
  // typing into a different label and we'd otherwise clobber their
  // input mid-keystroke.
  const [localLabelsDraft, setLocalLabelsDraft] = useState<AssetLabel[] | null>(null);
  const localLabelsSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!row) return;
    if (localLabelsSaveTimerRef.current !== null) return;
    setLocalLabelsDraft(null);
  }, [row?.localAssetLabels]);

  useEffect(
    () => () => {
      if (localLabelsSaveTimerRef.current) {
        clearTimeout(localLabelsSaveTimerRef.current);
      }
    },
    [],
  );

  const effectiveLocalLabels = localLabelsDraft ?? row?.localAssetLabels ?? [];

  const handleLocalLabelsChange = (next: AssetLabel[]) => {
    setLocalLabelsDraft(next);
    if (localLabelsSaveTimerRef.current) {
      clearTimeout(localLabelsSaveTimerRef.current);
    }
    localLabelsSaveTimerRef.current = setTimeout(() => {
      localLabelsSaveTimerRef.current = null;
      inlinePatchMut.mutate({ localAssetLabels: next });
    }, 800);
  };

  // OrganicAssets §9.3: Organic mirror of the Local labels draft + debounce.
  const [organicLabelsDraft, setOrganicLabelsDraft] = useState<AssetLabel[] | null>(null);
  const organicLabelsSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!row) return;
    if (organicLabelsSaveTimerRef.current !== null) return;
    setOrganicLabelsDraft(null);
  }, [row?.organicAssetLabels]);

  useEffect(
    () => () => {
      if (organicLabelsSaveTimerRef.current) {
        clearTimeout(organicLabelsSaveTimerRef.current);
      }
    },
    [],
  );

  const effectiveOrganicLabels = organicLabelsDraft ?? row?.organicAssetLabels ?? [];

  const handleOrganicLabelsChange = (next: AssetLabel[]) => {
    setOrganicLabelsDraft(next);
    if (organicLabelsSaveTimerRef.current) {
      clearTimeout(organicLabelsSaveTimerRef.current);
    }
    organicLabelsSaveTimerRef.current = setTimeout(() => {
      organicLabelsSaveTimerRef.current = null;
      inlinePatchMut.mutate({ organicAssetLabels: next });
    }, 800);
  };

  // OrganicAssets §8 fix: stub auto-creation handlers, wired on BOTH the
  // view and edit Local/Organic browsers so default labels populate on any
  // render where the folder has files (not only after the AM clicks Edit).
  // On success we refetch the row so the new labels appear in the UI, not
  // just Firestore. Idempotent server-side; the grid's fingerprint guard
  // keeps it to one write per file set.
  const persistLocalStubs = (stubs: AssetLabel[]) => {
    addRowLocalAssetLabelStubs(rolloutId!, rowId!, stubs)
      .then(() => {
        qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'row', rowId] });
      })
      .catch((err) => console.warn('[label-default] Local stub-write failed', err));
  };
  const persistOrganicStubs = (stubs: AssetLabel[]) => {
    addRowOrganicAssetLabelStubs(rolloutId!, rowId!, stubs)
      .then(() => {
        qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'row', rowId] });
      })
      .catch((err) => console.warn('[label-default] Organic stub-write failed', err));
  };

  // Budget mutations (Fix 4). Mirror the master grid: Monthly fires the
  // bidirectional drift modal on primary rows, secondaries write through
  // directly; Increase always direct-writes via the inline patchRow.
  // Drift state is local to this surface (parallel implementation per the
  // session plan; no premature shared hook).
  const budgetMut = useMutation({
    mutationFn: async (args: {
      platform: 'meta' | 'google';
      monthlyCents: number | null;
      patchAccount: boolean;
    }) => {
      if (!rolloutId || !rowId) throw new Error('Missing rollout or row id');
      return patchRowBudget(rolloutId, rowId, args);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'row', rowId] });
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (err: Error) => {
      setSaveError(err instanceof ApiError ? err.message : err.message);
    },
  });
  const increaseMut = useMutation({
    mutationFn: async (patch: {
      metaCampaignIncrease?: number;
      googleCampaignIncrease?: number;
    }) => {
      if (!rolloutId || !rowId) throw new Error('Missing rollout or row id');
      return patchRow(rolloutId, rowId, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'row', rowId] });
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
    },
    onError: (err: Error) => {
      setSaveError(err instanceof ApiError ? err.message : err.message);
    },
  });


  // Save Alternate as Campaign (Pre-P2b-3 §5.4). Server creates a new
  // Campaign + repoints the row. On success: invalidate row + grid +
  // campaigns list, close modal, exit edit mode (the row now references
  // a real Campaign — there's nothing left to "save customisation of").
  const saveAsMut = useMutation({
    mutationFn: async (args: {
      name: string;
      description: string | null;
      shareTargets: Array<{ accountId: string }>;
    }) => {
      if (!rolloutId || !rowId) throw new Error('Missing rollout or row id');
      return saveAsCampaign(rolloutId, rowId, args);
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'row', rowId] });
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      setSaveAsOpen(false);
      setSaveAsError(null);
      setDraft(null);
      setMode('read');
      // Pre-P2b-3 polish (Fix 1): toast covers both branches. activated
      // = empty validationWarnings → "saved and activated". Otherwise
      // → "saved as draft, N field(s) need more content" with a link
      // to the new Campaign so the AM can fix the gaps.
      setSaveAsToast({
        campaignId: result.campaign.id,
        activated: result.validationWarnings.length === 0,
        warnings: result.validationWarnings,
      });
    },
    onError: (err: Error) => {
      setSaveAsError(err instanceof ApiError ? err.message : err.message);
    },
  });

  const refreshMut = useMutation({
    mutationFn: async () => {
      if (!rolloutId || !rowId) throw new Error('Missing rollout or row id');
      return refreshRowFromCampaign(rolloutId, rowId);
    },
    onSuccess: (nextRow) => {
      // Replace the cached row immediately so the useEffect above picks up
      // the refreshed snapshot without a round-trip. Invalidate the grid
      // cache too so the master view catches the contentLastEditedAt tick
      // if/when it ever surfaces the field.
      qc.setQueryData(['rollout', rolloutId, 'row', rowId], nextRow);
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
      setDraft(initialEditState(nextRow));
      setRefreshOpen(false);
    },
    onError: (err: Error) => {
      setSaveError(err instanceof ApiError ? err.message : err.message);
      setRefreshOpen(false);
    },
  });

  if (rowQuery.isLoading || rolloutQuery.isLoading) return <CenteredSpinner />;

  if (rowQuery.error || !row) {
    return (
      <div className="text-red-600 text-sm">
        Row not found or failed to load.
      </div>
    );
  }
  if (!rolloutQuery.data) {
    return (
      <div className="text-red-600 text-sm">
        Rollout not found for this row.
      </div>
    );
  }

  const rollout = rolloutQuery.data;
  if (row.rolloutId !== rollout.id) {
    return (
      <div className="text-red-600 text-sm">
        Row does not belong to this rollout.
      </div>
    );
  }

  // Still loading account / profiles? Show spinner rather than render a
  // partial page -- the preview needs the location context to resolve
  // templates correctly.
  if (accountQuery.isLoading || locationProfileQuery.isLoading) {
    return <CenteredSpinner />;
  }
  if (accountQuery.error || !accountQuery.data) {
    return (
      <div className="text-red-600 text-sm">
        Location Account not found or failed to load.
      </div>
    );
  }

  const account = accountQuery.data.account;
  const locationProfile = locationProfileQuery.data ?? null;
  const franchiseProfile = franchiseProfileQuery.data ?? null;
  const campaign: Campaign | null = campaignQuery.data ?? null;
  const users = usersQuery.data ?? [];
  const assignedAm = row.assignedAmId ? users.find((u) => u.uid === row.assignedAmId) ?? null : null;
  // Round-1 polish Fix 6: per-row builders. Read-only here — edits
  // happen on the relevant role grid. Always shown ("Unassigned" when
  // null) so the AM has a quick reference.
  const metaBuilder = row.metaBuilderId
    ? users.find((u) => u.uid === row.metaBuilderId) ?? null
    : null;
  const googleBuilder = row.googleBuilderId
    ? users.find((u) => u.uid === row.googleBuilderId) ?? null
    : null;

  const ctx: ResolveContext = {
    account: { name: account.name, slug: account.slug },
    locationProfile,
    franchiseProfile,
  };

  const status = computeAccountCampaignStatus(row as RowForStatus, account.status);
  const archived = rollout.status === 'archived';
  const isOptOut = row.rowType === 'opt_out';
  const isUnassigned = row.rowType === 'unassigned';

  const editDisabledReason = (() => {
    if (archived) return 'Rollout is archived — editing is disabled.';
    if (isOptOut) return 'Opt-out rows have no editable content.';
    if (isUnassigned) return 'Assign a Campaign first.';
    return null;
  })();

  const locationLabel =
    (locationProfile?.franchiseSuburb && locationProfile.franchiseSuburb.trim()) || account.name;

  const editing = mode === 'edit' && draft !== null;
  const updateDraft = <K extends keyof EditState>(key: K, value: EditState[K]) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  // Daily-budget rendering goes through DailyBudgetCell (same component
  // the master grid uses) so the breakdown popover and the click-to-detail
  // "modified" popover work identically here. The cell internally calls
  // computeDailyBudget; we just need the modified flag at this layer.
  const metaBudgetModified = isBudgetModified(row, 'meta');
  const googleBudgetModified = isBudgetModified(row, 'google');

  // Pre-P2b-3 §2 + §3 effective platform flags. Account-level flag plus
  // the per-row override (null inherits, true forces on, false forces off).
  // Third layer: the campaign's own channels (Meta only / Google only).
  const metaActive = effectiveRunsMeta(account.runsMeta, row.metaEnabled, campaign?.channels);
  const googleActive = effectiveRunsGoogle(
    account.runsGoogle,
    row.googleEnabled,
    campaign?.channels,
  );

  // Pre-P2b-3 polish round 5 §1: resolve which Meta ad type this row
  // actually runs. Drives the Meta Content section's gating: when the
  // result is null (Campaign 'both' + no row override + no location
  // default), the ad-copy controls hide behind a "Pick an ad type"
  // prompt. Helper handles defensive nulls; gives null when Campaign
  // is unavailable too (transient query miss → behave like null).
  const effectiveAdType: EffectiveMetaAdType = campaign
    ? effectiveMetaAdType(row, campaign, locationProfile)
    : null;
  const campaignMetaAdType = campaign?.metaAdCopy.metaAdType ?? null;

  // Monthly commit handler (Fix 4). Mirrors the master grid's drift-vs-
  // direct decision: secondaries always direct-write (no anchoring
  // invariant); primaries that drift from the Account retainer open the
  // bidirectional modal. Defined here (after the loading guards) so it
  // closes over the resolved `row` + `account`.
  const commitMonthly = (platform: 'meta' | 'google', nextCents: number | null) => {
    if (archived) return;
    const currentRowCents =
      platform === 'meta' ? row.metaMonthlyBudget : row.googleMonthlyBudget;
    if (!row.primary) {
      budgetMut.mutate({ platform, monthlyCents: nextCents, patchAccount: false });
      return;
    }
    const accountCents =
      platform === 'meta' ? account.metaMasterMonthlyBudget : account.googleMasterMonthlyBudget;
    if (nextCents !== accountCents) {
      setDrift({ platform, fromCents: currentRowCents, toCents: nextCents });
    } else {
      budgetMut.mutate({ platform, monthlyCents: nextCents, patchAccount: false });
    }
  };
  const PLATFORM_DISABLED_SECTIONS = new Set<string>(
    [
      ...(!metaActive ? (['re-meta-content', 're-meta-execution'] as const) : []),
      ...(!googleActive ? (['re-google-content', 're-google-execution'] as const) : []),
    ],
  );

  // Meta campaign naming preview (Pre-P2b-3 §1; polish round 2 Item 1).
  // Pulls descriptor + template from the row's snapshot first, falling
  // back to the Campaign when the row's value is null/undefined
  // (legacy / inherit). Template fallback walks Campaign override →
  // Brand default → null. monthYear precedence: row.startDate →
  // rollout.defaultStartDate → today.
  //
  // Edit-mode: while the AM is editing the descriptor / template, the
  // preview reflects the LIVE draft so they can see substitution
  // changes without saving. Read-mode: it reflects the saved row.
  // Explicit empty string substitutes literally per Q4 — that's the
  // AM's choice (and visible in the preview as awkward double spaces).
  const metaNamingPreview = (() => {
    if (!campaign) return { template: null as string | null, resolved: null as string | null };
    const draftDesc = draft?.campaignDescriptor;
    const draftTpl = draft?.metaCampaignNameTemplate;
    // Descriptor source: live draft (when editing) → row snapshot →
    // Campaign default. null/undefined at any tier falls through.
    const descriptorSource: string | null | undefined = editing
      ? draftDesc
      : row.campaignDescriptor;
    const descriptor =
      descriptorSource === null || descriptorSource === undefined
        ? campaign.campaignDescriptor ?? null
        : descriptorSource;
    // Template source: live draft (when editing) → row snapshot. When
    // null/undefined, walk Campaign → brand profile via the existing
    // helper.
    const templateSource: string | null | undefined = editing
      ? draftTpl
      : row.metaCampaignNameTemplate;
    const template =
      templateSource === null || templateSource === undefined
        ? effectiveMetaCampaignNameTemplate(campaign, franchiseProfile)
        : templateSource;
    if (!template) return { template: null, resolved: null };
    // Pre-P2b-3 polish round 5 §2.3: monthYear comes from the
    // rollout-level label, not from the row's startDate. One canonical
    // value per rollout, every row inherits.
    const monthYear = resolveMonthYear(rollout.monthYearLabel);
    const { output } = renderTemplate(
      template,
      ctx.account,
      ctx.locationProfile,
      ctx.franchiseProfile,
      {
        campaignDescriptor: descriptor,
        monthYear,
      },
    );
    return { template, resolved: output };
  })();

  // Pre-P2b-3 polish round 5 §2.2 + §2.5: Google ad-label preview.
  // Mirrors the Meta naming preview above — same descriptor source
  // (Overview-level), same monthYear (rollout-level), Google-specific
  // template source. Empty-string substitution semantics identical.
  const googleAdLabelPreview = (() => {
    if (!campaign) return { template: null as string | null, resolved: null as string | null };
    const draftDesc = draft?.campaignDescriptor;
    const draftTpl = draft?.googleAdLabelTemplate;
    const descriptorSource: string | null | undefined = editing
      ? draftDesc
      : row.campaignDescriptor;
    const descriptor =
      descriptorSource === null || descriptorSource === undefined
        ? campaign.campaignDescriptor ?? null
        : descriptorSource;
    const templateSource: string | null | undefined = editing
      ? draftTpl
      : row.googleAdLabelTemplate;
    const template =
      templateSource === null || templateSource === undefined
        ? effectiveGoogleAdLabelTemplate(campaign, franchiseProfile)
        : templateSource;
    if (!template) return { template: null, resolved: null };
    const monthYear = resolveMonthYear(rollout.monthYearLabel);
    const { output } = renderTemplate(
      template,
      ctx.account,
      ctx.locationProfile,
      ctx.franchiseProfile,
      {
        campaignDescriptor: descriptor,
        monthYear,
      },
    );
    return { template, resolved: output };
  })();

  const fmtDate = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="grid grid-cols-[200px_1fr] gap-6 max-w-6xl">
      {/* Round 2: replaced the local sticky <nav> with the shared
          StickyNav primitive — adds scroll-spy that the previous
          inline implementation lacked. PLATFORM_DISABLED_SECTIONS
          now feeds the `visible` flag so disabled-platform sections
          (e.g. Meta content on a Google-only Account) drop out of
          the menu entirely. The page section still renders with
          its existing "Account doesn't run X" placeholder so
          buyers who scroll there still get the explanation. */}
      <StickyNav
        ariaLabel="Row editor sections"
        sections={SECTION_NAV.map((s) => ({
          ...s,
          visible: !PLATFORM_DISABLED_SECTIONS.has(s.id),
        }))}
        actions={
          // Round 3 (Fix 2): mirror the top-bar Save / Cancel
          // buttons inside the sticky nav, but ONLY in edit mode —
          // the read-mode top bar has Edit / Back, no Save concept.
          // Same handlers and same disabled state as the top bar so
          // the two paths can't drift. Top-bar buttons stay in
          // place; sticky-nav copies are an additional surface
          // for editors deep in long content sections.
          mode === 'edit' ? (
            <>
              <Button
                type="button"
                onClick={() => saveMut.mutate()}
                disabled={
                  saveMut.isPending || refreshMut.isPending || saveAsMut.isPending
                }
                className="w-full"
              >
                {saveMut.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={cancelEdit}
                disabled={
                  saveMut.isPending || refreshMut.isPending || saveAsMut.isPending
                }
                className="w-full"
              >
                Cancel
              </Button>
            </>
          ) : null
        }
      />

      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs text-slate-500">
              <Link to="/rollouts" className="hover:underline">
                Rollouts
              </Link>
              <span className="mx-1 text-slate-300">/</span>
              <Link to={`/rollouts/${rollout.id}`} className="hover:underline">
                {rollout.name}
              </Link>
              <span className="mx-1 text-slate-300">/</span>
              <span>{locationLabel}</span>
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">
              {locationLabel}
              {!row.primary && (
                <span className="ml-2 text-base text-slate-400">(secondary row)</span>
              )}
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${statusPillClass(status)}`}>
                {status}
              </span>
              {campaign && (
                <span className="text-sm text-slate-600">
                  Campaign:{' '}
                  <Link to={`/campaigns/${campaign.id}`} className="hover:underline">
                    {resolveDisplayCampaignName(row, campaign)}
                  </Link>
                  {row.campaignNameOverride && (
                    <span
                      className="ml-1 text-xs italic text-amber-700"
                      title={`Underlying Campaign: ${campaign.name}`}
                    >
                      (custom name)
                    </span>
                  )}
                  {campaign.isOptOut && <span className="ml-1 text-xs text-rose-700">(Opt Out)</span>}
                  {campaign.isAlternate && <span className="ml-1 text-xs text-indigo-700">(Alt)</span>}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mode === 'read' ? (
              <>
                <Link to={`/rollouts/${rollout.id}`}>
                  <Button type="button" variant="outline">
                    ← Back to grid
                  </Button>
                </Link>
                {/* Ad Preview Stage 3 §3.1: Send Preview on the Row
                    Editor read view. Only shown on previewable rows
                    (campaign rowType + assigned Campaign); disabled
                    with a tooltip until a preview link exists. */}
                {row.rowType === 'campaign' && row.campaignId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSendOpen(true)}
                    disabled={!row.latestPreviewLinkId}
                    title={
                      row.latestPreviewLinkId
                        ? 'Email / SMS this preview to the franchisee contacts'
                        : 'Generate a preview first (Build View), then you can send it.'
                    }
                  >
                    Send Preview
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={enterEdit}
                  disabled={editDisabledReason !== null}
                  title={editDisabledReason ?? undefined}
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                {campaign?.isAlternate && row.rowType === 'campaign' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSaveAsError(null);
                      setSaveAsOpen(true);
                    }}
                    // Pre-P2b-3 polish round 3 Fix 2: also disable
                    // when the draft has unsaved edits — saveAs reads
                    // the row's server-side state, so unsaved tweaks
                    // (like a freshly-typed descriptor) would silently
                    // drop from the new Campaign.
                    disabled={
                      saveMut.isPending ||
                      refreshMut.isPending ||
                      saveAsMut.isPending ||
                      draftHasUnsavedChanges
                    }
                    title={
                      draftHasUnsavedChanges
                        ? 'Save your edits first so they flow into the new Campaign.'
                        : 'Save this customised Alternate as a real, reusable Campaign.'
                    }
                  >
                    🔖 Save Campaign for reuse
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRefreshOpen(true)}
                  disabled={saveMut.isPending || refreshMut.isPending || saveAsMut.isPending}
                  title={
                    diffSet.size === 0
                      ? 'No fields currently differ from the Campaign.'
                      : `${diffSet.size} field${diffSet.size === 1 ? '' : 's'} will be reset.`
                  }
                >
                  Refresh from Campaign
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelEdit}
                  disabled={saveMut.isPending || refreshMut.isPending || saveAsMut.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => saveMut.mutate()}
                  disabled={saveMut.isPending || refreshMut.isPending || saveAsMut.isPending}
                >
                  {saveMut.isPending ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </div>
        </div>

        {saveError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {saveError}
          </div>
        )}

        {isOptOut && (
          <div className="rounded-md border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-700">
            <strong>This row is currently Opted Out.</strong> Content editing is disabled.
            Reverse the opt-out from the master grid's Actions menu to return the row to
            unassigned.
          </div>
        )}

        {isUnassigned && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <strong>This row has no Campaign assigned.</strong> Assign one from the master grid's
            Campaign column. The editor shows the row's current (empty) state until then.
          </div>
        )}

        {archived && (
          <div className="rounded-md border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-700">
            This rollout is archived. Editing is disabled.
          </div>
        )}

        <SectionShell id="re-overview" title="Overview">
          <dl>
            <Field label="Location" value={account.name} />
            <Field
              label="Group tag"
              value={
                <em className="text-slate-400">
                  Resolved from account hierarchy on the master grid.
                </em>
              }
            />
            <Field
              label="Account manager"
              value={assignedAm ? formatUserDisplayName(assignedAm) : null}
            />
            {/* Round-1 polish Fix 6: per-row builders, read-only here.
                Edits live on the relevant role grid (Meta or Google). */}
            <Field
              label="Meta builder"
              value={
                metaBuilder ? (
                  formatUserDisplayName(metaBuilder)
                ) : (
                  <span className="text-slate-400">Unassigned</span>
                )
              }
            />
            <Field
              label="Google builder"
              value={
                googleBuilder ? (
                  formatUserDisplayName(googleBuilder)
                ) : (
                  <span className="text-slate-400">Unassigned</span>
                )
              }
            />
            <Field
              label="Campaign name"
              value={
                <CampaignNameField
                  row={row}
                  campaign={campaign}
                  archived={archived}
                  busy={inlinePatchMut.isPending}
                  onCommit={(next) =>
                    inlinePatchMut.mutate({ campaignNameOverride: next })
                  }
                  editing={editing}
                />
              }
            />
            {/* Pre-P2b-3 polish round 5 §2.5: descriptor moved from
                Meta Content to Overview. Shared between Meta naming
                and Google ad-label templates — single value at the
                row level (snapshotted from Campaign at create,
                editable on the row's draft Save flow). The badge
                shows when the row's snapshot drifts from the
                Campaign's current authored value. */}
            <Field
              label="Campaign descriptor"
              modified={diffSet.has('campaignDescriptor')}
              value={
                editing && draft ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      placeholder={
                        campaign?.campaignDescriptor
                          ? `Inherit from Campaign: ${campaign.campaignDescriptor}`
                          : 'Campaign has no descriptor set'
                      }
                      value={draft.campaignDescriptor ?? ''}
                      onChange={(e) =>
                        updateDraft('campaignDescriptor', e.target.value)
                      }
                    />
                    {draft.campaignDescriptor !== null && (
                      <button
                        type="button"
                        className="text-xs text-slate-500 hover:text-slate-900 hover:underline"
                        onClick={() => updateDraft('campaignDescriptor', null)}
                        title="Inherit from Campaign — clears the row override"
                      >
                        Use Campaign default
                      </button>
                    )}
                  </div>
                ) : (
                  row.campaignDescriptor ??
                  (campaign?.campaignDescriptor ? (
                    <span className="text-slate-400 italic">
                      Inherited from Campaign: {campaign.campaignDescriptor}
                    </span>
                  ) : null)
                )
              }
            />
            <Field
              label="Run on Meta"
              value={
                <PlatformOverrideControl
                  platform="meta"
                  override={row.metaEnabled}
                  accountFlag={account.runsMeta ?? true}
                  busy={inlinePatchMut.isPending}
                  disabled={archived}
                  onChange={(next) =>
                    inlinePatchMut.mutate({ metaEnabled: next })
                  }
                />
              }
            />
            <Field
              label="Run on Google"
              value={
                <PlatformOverrideControl
                  platform="google"
                  override={row.googleEnabled}
                  accountFlag={account.runsGoogle ?? true}
                  busy={inlinePatchMut.isPending}
                  disabled={archived}
                  onChange={(next) =>
                    inlinePatchMut.mutate({ googleEnabled: next })
                  }
                />
              }
            />
            {/* Pre-P2b-3 polish round 3 (Fix 3): always-on editable
                dates, click-and-go via the same DatePickerCell the
                master grid uses. Edit fires inlinePatchMut directly —
                the row editor's draft Save button doesn't touch dates.
                Disabled when the rollout is archived.
                Round 4: ModifiedBadge fires next to the label when the
                date has drifted from its creation-time *Original
                anchor, matching the existing content-modification
                badge pattern in this editor. */}
            <Field
              label="Start date"
              modified={hasStartDateDrift(row)}
              value={
                <DatePickerCell
                  valueIso={row.startDate}
                  disabled={archived}
                  onSubmit={async (iso) => {
                    await inlinePatchMut.mutateAsync({ startDate: iso });
                  }}
                  ariaLabel="Start date"
                />
              }
            />
            <Field
              label="End date"
              modified={hasEndDateDrift(row)}
              value={
                <DatePickerCell
                  valueIso={row.endDate}
                  disabled={archived}
                  onSubmit={async (iso) => {
                    await inlinePatchMut.mutateAsync({ endDate: iso });
                  }}
                  ariaLabel="End date"
                />
              }
            />
            <Field
              label="Meta ready to build"
              value={row.metaReadyToBuild ? 'Yes' : 'No'}
            />
            <Field
              label="Google ready to build"
              value={row.googleReadyToBuild ? 'Yes' : 'No'}
            />
            <Field
              label="Offer mechanics URL"
              value={
                <PlanOfferUrl
                  url={planOfferMechanicsUrl}
                  ariaLabel="Offer mechanics URL"
                />
              }
            />
            <Field
              label="Kyck campaign overview URL"
              value={
                <PlanOfferUrl
                  url={planKyckCampaignOverviewUrl}
                  ariaLabel="Kyck campaign overview URL"
                />
              }
            />
            <Field
              label="Row notes"
              value={
                <RowNotesField
                  row={row}
                  archived={archived}
                  busy={inlinePatchMut.isPending}
                  onCommit={(next) => inlinePatchMut.mutate({ rowNotes: next })}
                />
              }
            />
          </dl>
          {editing && (
            <p className="text-xs text-slate-500 mt-4">
              Dates, AM, and ready flags are edited from the master grid — Campaign
              content here, budgets directly above, notes here, local assets in the
              Assets section.
            </p>
          )}
        </SectionShell>

        <SectionShell id="re-budget" title="Budget">
          <dl>
            <Field
              label="Meta monthly budget"
              value={
                <MonthlyBudgetInput
                  // Force a remount on row change so defaultValue tracks
                  // the latest snapshot rather than caching stale text.
                  key={`meta-monthly-${row.id}-${row.metaMonthlyBudget ?? 'null'}`}
                  valueCents={row.metaMonthlyBudget}
                  disabled={archived || !metaActive}
                  widthClass="w-32"
                  ariaLabel="Meta monthly budget"
                  onCommit={(cents) => commitMonthly('meta', cents)}
                />
              }
            />
            <Field
              label="Meta campaign increase"
              value={
                <IncreaseInput
                  key={`meta-increase-${row.id}-${row.metaCampaignIncrease}`}
                  valueCents={row.metaCampaignIncrease}
                  disabled={archived || !metaActive}
                  widthClass="w-32"
                  ariaLabel="Meta campaign increase"
                  onCommit={(cents) =>
                    increaseMut.mutate({ metaCampaignIncrease: cents })
                  }
                />
              }
            />
            <Field
              label="Meta daily"
              value={
                <DailyBudgetCell
                  row={row}
                  platform="meta"
                  currency={account.currency}
                  modified={metaBudgetModified}
                  accountMonthlyBudget={account.metaMasterMonthlyBudget}
                  disabled={!metaActive}
                />
              }
            />
            <Field
              label="Google monthly budget"
              value={
                <MonthlyBudgetInput
                  key={`google-monthly-${row.id}-${row.googleMonthlyBudget ?? 'null'}`}
                  valueCents={row.googleMonthlyBudget}
                  disabled={archived || !googleActive}
                  widthClass="w-32"
                  ariaLabel="Google monthly budget"
                  onCommit={(cents) => commitMonthly('google', cents)}
                />
              }
            />
            <Field
              label="Google campaign increase"
              value={
                <IncreaseInput
                  key={`google-increase-${row.id}-${row.googleCampaignIncrease}`}
                  valueCents={row.googleCampaignIncrease}
                  disabled={archived || !googleActive}
                  widthClass="w-32"
                  ariaLabel="Google campaign increase"
                  onCommit={(cents) =>
                    increaseMut.mutate({ googleCampaignIncrease: cents })
                  }
                />
              }
            />
            <Field
              label="Google daily"
              value={
                <DailyBudgetCell
                  row={row}
                  platform="google"
                  currency={account.currency}
                  modified={googleBudgetModified}
                  accountMonthlyBudget={account.googleMasterMonthlyBudget}
                  disabled={!googleActive}
                />
              }
            />
          </dl>
        </SectionShell>

        <SectionShell id="re-meta-content" title="Meta content">
          {PLATFORM_DISABLED_SECTIONS.has('re-meta-content') ? (
            <p className="text-sm text-slate-400 italic">
              Account doesn't run Meta — content is hidden on this row.
            </p>
          ) : (
          <>
          {/* Pre-P2b-3 polish round 5 §2.5: descriptor moved to
              Overview (shared with Google labels). Meta Content keeps
              just the template editor + Meta-resolved preview line.
              Edit mode: text input with placeholder showing the
              effective inherited template + "Use brand default"
              affordance to clear the row override. Read mode: Field
              row showing the explicit value or muted "Inherited:
              <template>". Resolved preview always visible. */}
          {editing && draft ? (
            <div className="mb-4 space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                  Meta campaign name template
                  {diffSet.has('metaCampaignNameTemplate') && <ModifiedBadge />}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
                    placeholder={
                      effectiveMetaCampaignNameTemplate(campaign, franchiseProfile) ??
                      'No naming template set'
                    }
                    value={draft.metaCampaignNameTemplate ?? ''}
                    onChange={(e) =>
                      updateDraft('metaCampaignNameTemplate', e.target.value)
                    }
                  />
                  {draft.metaCampaignNameTemplate !== null && (
                    <button
                      type="button"
                      className="text-xs text-slate-500 hover:text-slate-900 hover:underline"
                      onClick={() => updateDraft('metaCampaignNameTemplate', null)}
                      title="Inherit from Campaign / brand — clears the row override"
                    >
                      Use brand default
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Supports <code className="font-mono">{'{locationName}'}</code>,{' '}
                  <code className="font-mono">{'{campaignDescriptor}'}</code>,{' '}
                  <code className="font-mono">{'{monthYear}'}</code> and the standard location /
                  brand placeholders. Descriptor is set in the Overview section.
                </p>
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Resolved Meta campaign name
                </div>
                {metaNamingPreview.template === null ? (
                  <span className="text-slate-400" title="No naming template set">
                    —
                  </span>
                ) : (
                  <code className="font-mono text-sm break-all text-slate-900">
                    {metaNamingPreview.resolved}
                  </code>
                )}
              </div>
            </div>
          ) : (
            <div className="mb-4 space-y-2">
              <dl>
                <Field
                  label="Name template"
                  modified={diffSet.has('metaCampaignNameTemplate')}
                  value={(() => {
                    const explicit = row.metaCampaignNameTemplate;
                    if (explicit !== null && explicit !== undefined) {
                      return (
                        <code className="font-mono text-xs break-all">{explicit}</code>
                      );
                    }
                    const inherited = effectiveMetaCampaignNameTemplate(
                      campaign,
                      franchiseProfile,
                    );
                    if (!inherited) return null;
                    return (
                      <span className="text-slate-400 italic">
                        Inherited:{' '}
                        <code className="font-mono text-xs break-all not-italic">
                          {inherited}
                        </code>
                      </span>
                    );
                  })()}
                />
              </dl>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Resolved Meta campaign name
                </div>
                {metaNamingPreview.template === null ? (
                  <span className="text-slate-400" title="No naming template set">
                    —
                  </span>
                ) : (
                  <code className="font-mono text-sm break-all text-slate-900">
                    {metaNamingPreview.resolved}
                  </code>
                )}
              </div>
            </div>
          )}

          {/* Pre-P2b-3 polish round 5 §1: Meta ad type widget. Always
              visible — locked label when Campaign declares the type,
              tri-state dropdown when Campaign is 'both'. Click-and-go
              via inlinePatchMut. The lead form / body copy gating
              below switches on the resolved effective type. */}
          {campaign && (
            <div className="mb-4">
              <Field
                label="Meta ad type"
                modified={diffSet.has('metaAdCopy.metaAdType')}
                value={
                  <MetaAdTypeControl
                    campaignMetaAdType={campaign.metaAdCopy.metaAdType}
                    rowOverride={row.metaAdType}
                    locationDefault={locationProfile?.defaultMetaAdType ?? null}
                    busy={inlinePatchMut.isPending}
                    disabled={archived}
                    onChange={(next) => inlinePatchMut.mutate({ metaAdType: next })}
                  />
                }
              />
            </div>
          )}

          {/* Pre-P2b-3 polish round 5 §1.4: when the effective ad type
              is null (Campaign 'both' + row null + location null),
              hide the editing controls. The override widget above
              still shows so the AM can pick. */}
          {effectiveAdType === null ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Pick an ad type to author content.
            </div>
          ) : editing && draft ? (
            <div className="space-y-5">
              {/* Modification badges stay next to labels even in edit mode.
                  The badge reflects the last-saved row vs Campaign; local
                  edits don't participate until Save lands.
                  Pre-P2b-3 polish round 5 §1: the Meta ad type editor
                  moved out of the snapshot draft — it's now an inline-
                  patch field, rendered above as MetaAdTypeControl. The
                  snapshot's metaAdCopy.metaAdType still drifts when
                  Campaign authoring changes; the modification badge
                  for that lives next to the widget. */}
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  {diffSet.has('metaAdCopy.bodyCopy') && <ModifiedBadge />}
                </div>
                <BodyCopyArrayEditor
                  values={draft.metaAdCopy.bodyCopy}
                  onChange={(v) =>
                    updateDraft('metaAdCopy', { ...draft.metaAdCopy, bodyCopy: v })
                  }
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  {diffSet.has('metaAdCopy.headlines') && <ModifiedBadge />}
                </div>
                <HeadlineArrayEditor
                  values={draft.metaAdCopy.headlines}
                  onChange={(v) =>
                    updateDraft('metaAdCopy', { ...draft.metaAdCopy, headlines: v })
                  }
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  {diffSet.has('metaAdCopy.descriptions') && <ModifiedBadge />}
                </div>
                <DescriptionArrayEditor
                  values={draft.metaAdCopy.descriptions}
                  onChange={(v) =>
                    updateDraft('metaAdCopy', { ...draft.metaAdCopy, descriptions: v })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                  CTA button
                  {diffSet.has('metaAdCopy.ctaButton') && <ModifiedBadge />}
                </label>
                <select
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={draft.metaAdCopy.ctaButton}
                  onChange={(e) =>
                    updateDraft('metaAdCopy', {
                      ...draft.metaAdCopy,
                      ctaButton: e.target.value as CtaOption,
                    })
                  }
                >
                  {CTA_OPTIONS.map((cta) => (
                    <option key={cta} value={cta}>
                      {cta}
                    </option>
                  ))}
                </select>
              </div>
              {/* Pre-P2b-3 polish round 5 §1.4: lead form fields are
                  gated by the resolved effective ad type — the row
                  override + location default + Campaign all roll up
                  through effectiveMetaAdType.
                  Multi Lead Form spec §1.2 + §5.2: the row carries its
                  own snapshot of a single CampaignLeadForm (top-level
                  draft.leadForm). The selector that controls WHICH
                  lead form this row tracks lives on the master grid /
                  Lead Form column — not here. Edit mode authors the
                  snapshot's name / formType / fields in place. */}
              {effectiveAdType === 'lead_gen' && draft.leadForm && (
                <div className="space-y-4 border-t border-slate-100 pt-4">
                  <LeadFormReassignBanner
                    row={row}
                    campaign={campaign}
                    archived={archived}
                    busy={inlinePatchMut.isPending}
                    onReassign={(id) => inlinePatchMut.mutate({ leadFormId: id })}
                  />
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      Lead form name
                      {diffSet.has('leadForm') && <ModifiedBadge />}
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                      value={draft.leadForm.name}
                      onChange={(e) =>
                        draft.leadForm &&
                        updateDraft('leadForm', { ...draft.leadForm, name: e.target.value })
                      }
                      maxLength={120}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      Lead form type
                      {diffSet.has('leadForm') && <ModifiedBadge />}
                    </label>
                    <select
                      className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                      value={draft.leadForm.formType}
                      onChange={(e) =>
                        draft.leadForm &&
                        updateDraft('leadForm', {
                          ...draft.leadForm,
                          formType: e.target.value as FormType,
                        })
                      }
                    >
                      {FORM_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-sm font-semibold text-slate-900">Lead form</h3>
                      {diffSet.has('leadForm') && <ModifiedBadge />}
                    </div>
                    <LeadFormEditor
                      value={draft.leadForm.fields}
                      onChange={(v) =>
                        draft.leadForm &&
                        updateDraft('leadForm', { ...draft.leadForm, fields: v })
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          ) : row.metaAdCopy ? (
            <dl>
              {/* Pre-P2b-3 polish round 5 §1: the Meta ad type field
                  moved out of this read-mode dl — now rendered above
                  as MetaAdTypeControl (always visible, snapshot drift
                  badge included). */}
              <Field
                label="Body copy"
                value={
                  <StringList
                    items={row.metaAdCopy.bodyCopy}
                    ctx={ctx}
                    templated
                    emptyLabel="(no body copy)"
                  />
                }
                modified={diffSet.has('metaAdCopy.bodyCopy')}
              />
              <Field
                label="Headlines"
                value={
                  <StringList
                    items={row.metaAdCopy.headlines}
                    ctx={ctx}
                    templated
                    emptyLabel="(no headlines)"
                  />
                }
                modified={diffSet.has('metaAdCopy.headlines')}
              />
              <Field
                label="Descriptions"
                value={
                  <StringList
                    items={row.metaAdCopy.descriptions}
                    ctx={ctx}
                    templated
                    emptyLabel="(none — optional)"
                  />
                }
                modified={diffSet.has('metaAdCopy.descriptions')}
              />
              <Field
                label="CTA button"
                value={row.metaAdCopy.ctaButton}
                modified={diffSet.has('metaAdCopy.ctaButton')}
              />
              {/* Pre-P2b-3 polish round 5 §1.4: read-mode lead form
                  block gated on effective ad type, parity with the
                  edit-mode gate above.
                  Multi Lead Form spec §1.2: read from the row's
                  top-level leadForm snapshot. Single `leadForm` diff
                  key drives every modified badge in this block. */}
              {effectiveAdType === 'lead_gen' && row.leadForm && (
                <>
                  <LeadFormReassignBanner
                    row={row}
                    campaign={campaign}
                    archived={archived}
                    busy={inlinePatchMut.isPending}
                    onReassign={(id) => inlinePatchMut.mutate({ leadFormId: id })}
                  />
                  <Field
                    label="Lead form name"
                    value={row.leadForm.name}
                    modified={diffSet.has('leadForm')}
                  />
                  <Field
                    label="Lead form type"
                    value={row.leadForm.formType}
                    modified={diffSet.has('leadForm')}
                  />
                  <Field
                    label="Lead form headline"
                    value={leadFormTextDisplay(row.leadForm.fields.headline, ctx)}
                    modified={diffSet.has('leadForm')}
                  />
                  <Field
                    label="Lead form description"
                    value={leadFormTextDisplay(row.leadForm.fields.description, ctx)}
                  />
                  <Field
                    label="Thank-you headline"
                    value={leadFormTextDisplay(row.leadForm.fields.thankYouHeadline, ctx)}
                  />
                  <Field
                    label="Thank-you description"
                    value={leadFormTextDisplay(row.leadForm.fields.thankYouDescription, ctx)}
                  />
                </>
              )}
            </dl>
          ) : (
            <p className="text-sm text-slate-400 italic">
              No Meta content snapshot on this row.
            </p>
          )}
          </>
          )}
        </SectionShell>

        <SectionShell id="re-google-content" title="Google content">
          {PLATFORM_DISABLED_SECTIONS.has('re-google-content') ? (
            <p className="text-sm text-slate-400 italic">
              Account doesn't run Google — content is hidden on this row.
            </p>
          ) : (
          <>
          {/* Pre-P2b-3 polish round 5 §2.5: Google ad-label template
              + Google-resolved preview. Parallel structure to the Meta
              naming block in the Meta Content section. Descriptor is
              shared (lives in Overview). */}
          {editing && draft ? (
            <div className="mb-4 space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                  Google ad label template
                  {diffSet.has('googleAdLabelTemplate') && <ModifiedBadge />}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
                    placeholder={
                      effectiveGoogleAdLabelTemplate(campaign, franchiseProfile) ??
                      'No Google label template set'
                    }
                    value={draft.googleAdLabelTemplate ?? ''}
                    onChange={(e) =>
                      updateDraft('googleAdLabelTemplate', e.target.value)
                    }
                  />
                  {draft.googleAdLabelTemplate !== null && (
                    <button
                      type="button"
                      className="text-xs text-slate-500 hover:text-slate-900 hover:underline"
                      onClick={() => updateDraft('googleAdLabelTemplate', null)}
                      title="Inherit from Campaign / brand — clears the row override"
                    >
                      Use brand default
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Same placeholder set as the Meta naming template. Descriptor is set
                  in the Overview section.
                </p>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Resolved Google ad label
                </div>
                {googleAdLabelPreview.template === null ? (
                  <span className="text-slate-400" title="No Google label template set">
                    —
                  </span>
                ) : (
                  <code className="font-mono text-sm break-all text-slate-900">
                    {googleAdLabelPreview.resolved}
                  </code>
                )}
              </div>
            </div>
          ) : (
            <div className="mb-4 space-y-2">
              <dl>
                <Field
                  label="Ad label template"
                  modified={diffSet.has('googleAdLabelTemplate')}
                  value={(() => {
                    const explicit = row.googleAdLabelTemplate;
                    if (explicit !== null && explicit !== undefined) {
                      return (
                        <code className="font-mono text-xs break-all">{explicit}</code>
                      );
                    }
                    const inherited = effectiveGoogleAdLabelTemplate(
                      campaign,
                      franchiseProfile,
                    );
                    if (!inherited) return null;
                    return (
                      <span className="text-slate-400 italic">
                        Inherited:{' '}
                        <code className="font-mono text-xs break-all not-italic">
                          {inherited}
                        </code>
                      </span>
                    );
                  })()}
                />
              </dl>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                  Resolved Google ad label
                </div>
                {googleAdLabelPreview.template === null ? (
                  <span className="text-slate-400" title="No Google label template set">
                    —
                  </span>
                ) : (
                  <code className="font-mono text-sm break-all text-slate-900">
                    {googleAdLabelPreview.resolved}
                  </code>
                )}
              </div>
            </div>
          )}

          {editing && draft ? (
            <div className="space-y-4">
              {/* Badges for the four sub-fields sit just above the editor so
                  authors can see which parts diverge from the Campaign. */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  Headlines
                  {diffSet.has('googleAssets.headlines') && <ModifiedBadge />}
                </span>
                <span className="flex items-center gap-1.5">
                  Descriptions
                  {diffSet.has('googleAssets.descriptions') && <ModifiedBadge />}
                </span>
                <span className="flex items-center gap-1.5">
                  Path 1
                  {diffSet.has('googleAssets.path1') && <ModifiedBadge />}
                </span>
                <span className="flex items-center gap-1.5">
                  Path 2
                  {diffSet.has('googleAssets.path2') && <ModifiedBadge />}
                </span>
              </div>
              <GoogleAssetsEditor
                value={draft.googleAssets}
                onChange={(v) => updateDraft('googleAssets', v)}
              />
            </div>
          ) : row.googleAssets ? (
            <dl>
              <Field
                label="Headlines"
                value={
                  <StringList
                    items={row.googleAssets.headlines.map((h) => h.text)}
                    pins={row.googleAssets.headlines.map((h) => h.pinPosition)}
                    ctx={ctx}
                    templated
                    emptyLabel="(no headlines)"
                  />
                }
                modified={diffSet.has('googleAssets.headlines')}
              />
              <Field
                label="Descriptions"
                value={
                  <StringList
                    items={row.googleAssets.descriptions}
                    ctx={ctx}
                    templated
                    emptyLabel="(no descriptions)"
                  />
                }
                modified={diffSet.has('googleAssets.descriptions')}
              />
              <Field
                label="Path 1"
                value={
                  row.googleAssets.path1 ? (
                    <code className="font-mono text-xs">
                      <Resolved template={row.googleAssets.path1} ctx={ctx} />
                    </code>
                  ) : null
                }
                modified={diffSet.has('googleAssets.path1')}
              />
              <Field
                label="Path 2"
                value={
                  row.googleAssets.path2 ? (
                    <code className="font-mono text-xs">
                      {/* Path2 is slugified post-render to match the
                          rendered output on the Google grid + Build
                          View — Google Ads paths reject spaces, so a
                          {shortSuburb} like "Botanic Ridge" becomes
                          "Botanic-Ridge" everywhere it surfaces. */}
                      {slugifyPath(
                        renderTemplate(
                          row.googleAssets.path2,
                          ctx.account,
                          ctx.locationProfile,
                          ctx.franchiseProfile,
                        ).output,
                      )}
                    </code>
                  ) : null
                }
                modified={diffSet.has('googleAssets.path2')}
              />
            </dl>
          ) : (
            <p className="text-sm text-slate-400 italic">
              No Google content snapshot on this row.
            </p>
          )}
          </>
          )}
        </SectionShell>

        <SectionShell
          id="re-utm"
          title="UTM config"
          badge={diffSet.has('utmConfig') ? <ModifiedBadge /> : null}
        >
          {editing && draft ? (
            <UtmConfigEditor
              value={draft.utmConfig}
              onChange={(v) => updateDraft('utmConfig', v)}
            />
          ) : row.utmConfig && row.utmConfig.enabled ? (
            <dl>
              <Field
                label="Platforms"
                value={
                  row.utmConfig.platforms.length === 0
                    ? '(none selected)'
                    : row.utmConfig.platforms.join(', ')
                }
              />
              <Field
                label="utm_campaign (id)"
                value={<Resolved template={row.utmConfig.shared.campaignId} ctx={ctx} />}
              />
              <Field
                label="Campaign name"
                value={<Resolved template={row.utmConfig.shared.campaignName} ctx={ctx} />}
              />
              {row.utmConfig.platforms.includes('meta') && row.utmConfig.meta && (
                <>
                  <Field
                    label="Meta source"
                    value={<Resolved template={row.utmConfig.meta.source || null} ctx={ctx} />}
                  />
                  <Field
                    label="Meta medium"
                    value={<Resolved template={row.utmConfig.meta.medium || null} ctx={ctx} />}
                  />
                  <Field
                    label="Meta term"
                    value={<Resolved template={row.utmConfig.meta.term || null} ctx={ctx} />}
                  />
                  <Field
                    label="Meta content"
                    value={<Resolved template={row.utmConfig.meta.content || null} ctx={ctx} />}
                  />
                </>
              )}
              {row.utmConfig.platforms.includes('google') && row.utmConfig.google && (
                <>
                  <Field
                    label="Google source"
                    value={<Resolved template={row.utmConfig.google.source || null} ctx={ctx} />}
                  />
                  <Field
                    label="Google medium"
                    value={<Resolved template={row.utmConfig.google.medium || null} ctx={ctx} />}
                  />
                  <Field
                    label="Google term"
                    value={<Resolved template={row.utmConfig.google.term || null} ctx={ctx} />}
                  />
                  <Field
                    label="Google content"
                    value={<Resolved template={row.utmConfig.google.content || null} ctx={ctx} />}
                  />
                </>
              )}
            </dl>
          ) : (
            <p className="text-sm text-slate-500">
              UTM appending is disabled on this row. Resolved landing URLs ship without UTM params.
            </p>
          )}
        </SectionShell>

        <SectionShell id="re-landing" title="Landing pages">
          {editing && draft ? (
            <div className="space-y-5">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                  Master landing type
                  {diffSet.has('landingPageType') && <ModifiedBadge />}
                </label>
                <select
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={draft.landingPageType}
                  onChange={(e) =>
                    updateDraft('landingPageType', e.target.value as LandingPageType)
                  }
                >
                  {LANDING_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              {draft.landingPageType === 'custom' && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                    Custom landing page URL
                    {diffSet.has('customLandingPageUrl') && <ModifiedBadge />}
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
                    value={draft.customLandingPageUrl}
                    onChange={(e) => updateDraft('customLandingPageUrl', e.target.value)}
                    placeholder="https://example.com/offer/{suburbSlug}"
                  />
                  <PlaceholderHint />
                </div>
              )}
              <div className="space-y-1 border-t border-slate-100 pt-4">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-semibold text-slate-900">Meta landing override</h3>
                  {diffSet.has('metaLandingPageOverride') && <ModifiedBadge />}
                </div>
                <LandingPageOverrideEditor
                  label=""
                  value={draft.metaLandingPageOverride}
                  onChange={(v) => updateDraft('metaLandingPageOverride', v)}
                  masterType={draft.landingPageType}
                  masterCustomUrl={draft.customLandingPageUrl || null}
                  templates={franchiseProfile?.landingPageTemplate ?? null}
                />
              </div>
              <div className="space-y-1 border-t border-slate-100 pt-4">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-semibold text-slate-900">Google landing override</h3>
                  {diffSet.has('googleLandingPageOverride') && <ModifiedBadge />}
                </div>
                <LandingPageOverrideEditor
                  label=""
                  value={draft.googleLandingPageOverride}
                  onChange={(v) => updateDraft('googleLandingPageOverride', v)}
                  masterType={draft.landingPageType}
                  masterCustomUrl={draft.customLandingPageUrl || null}
                  templates={franchiseProfile?.landingPageTemplate ?? null}
                />
              </div>
            </div>
          ) : (
            <dl>
              <Field
                label="Master landing type"
                value={row.landingPageType ?? null}
                modified={diffSet.has('landingPageType')}
              />
              <Field
                label="Master custom URL"
                value={
                  row.customLandingPageUrl ? (
                    <code className="font-mono text-xs break-all">
                      <Resolved template={row.customLandingPageUrl} ctx={ctx} />
                    </code>
                  ) : null
                }
                modified={diffSet.has('customLandingPageUrl')}
              />
              <Field
                label="Meta landing override"
                value={landingOverrideDisplay(row.metaLandingPageOverride)}
                modified={diffSet.has('metaLandingPageOverride')}
              />
              <Field
                label="Google landing override"
                value={landingOverrideDisplay(row.googleLandingPageOverride)}
                modified={diffSet.has('googleLandingPageOverride')}
              />
            </dl>
          )}
        </SectionShell>

        <SectionShell id="re-meta-execution" title="Meta execution">
          {PLATFORM_DISABLED_SECTIONS.has('re-meta-execution') ? (
            <p className="text-sm text-slate-400 italic">
              Account doesn't run Meta — execution fields are not tracked on this row.
            </p>
          ) : (
          <dl>
            <Field label="Meta main built" value={row.metaBuilt ? 'Yes' : 'No'} />
            <Field label="Meta local built" value={row.metaLocalBuilt ? 'Yes' : 'No'} />
            <Field label="Meta turned on" value={row.metaTurnedOn ? 'Yes' : 'No'} />
            <Field
              label="Meta post-rollout check"
              value={row.metaPostRolloutCheck ? 'Yes' : 'No'}
            />
            <Field
              label="Meta preview links"
              value={
                row.metaPreviewLinks ? (
                  <p className="whitespace-pre-wrap font-mono text-xs break-all">
                    {row.metaPreviewLinks}
                  </p>
                ) : null
              }
            />
            {/* Round-1 polish Fix 4: notes are shared via the
                Overview-level row notes — no per-platform Notes
                field here anymore. */}
          </dl>
          )}
        </SectionShell>

        <SectionShell id="re-google-execution" title="Google execution">
          {PLATFORM_DISABLED_SECTIONS.has('re-google-execution') ? (
            <p className="text-sm text-slate-400 italic">
              Account doesn't run Google — execution fields are not tracked on this row.
            </p>
          ) : (
          <dl>
            <Field label="Google main built" value={row.googleBuilt ? 'Yes' : 'No'} />
            <Field label="Google scheduled" value={row.googleScheduled ? 'Yes' : 'No'} />
            <Field
              label="Google post-rollout check"
              value={row.googlePostRolloutCheck ? 'Yes' : 'No'}
            />
            <Field
              label="Google preview links"
              value={
                row.googlePreviewLinks ? (
                  <p className="whitespace-pre-wrap font-mono text-xs break-all">
                    {row.googlePreviewLinks}
                  </p>
                ) : null
              }
            />
            {/* Round-1 polish Fix 4: shared rowNotes — no per-platform
                Notes field here anymore. */}
          </dl>
          )}
        </SectionShell>

        <SectionShell id="re-assets" title="Assets">
          <dl>
            {/* Meta polish Item 3: snapshot, read-only on the row (not
                row-editable — edits happen on the Campaign, Refresh
                propagates). Shown above the Meta assets folder. */}
            <Field
              label="Meta Creative Folder Name"
              value={
                row.metaCreativeFolderName?.trim() ? (
                  row.metaCreativeFolderName
                ) : (
                  <span className="text-slate-400">—</span>
                )
              }
            />
            <Field
              label="Meta assets folder"
              value={
                editing && draft ? (
                  <input
                    type="text"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
                    value={draft.metaAssetsFolderUrl}
                    onChange={(e) => updateDraft('metaAssetsFolderUrl', e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/..."
                  />
                ) : row.metaAssetsFolderUrl ? (
                  // P2c polish round 1 (Fix 2): when not editing, swap the
                  // plain link + static label list for the inline browser.
                  // Rolls the URL display, label list, lightbox, refresh,
                  // and Download All into one component. The browser itself
                  // surfaces the URL via Open in Drive / Download All so we
                  // suppress the redundant <a> here.
                  <DriveAssetsBrowser
                    mode="view"
                    title="Meta Assets"
                    folderUrl={row.metaAssetsFolderUrl}
                    value={row.metaAssetLabels ?? []}
                    // Ad Preview Mockups Stage 1 §2.3: read-only badge in
                    // the row editor's snapshot view.
                    showAdType
                  />
                ) : null
              }
              modified={diffSet.has('metaAssetsFolderUrl')}
            />
            <Field
              label="Local assets (per-location)"
              value={
                <div className="space-y-3">
                  <LocalAssetsField
                    row={row}
                    archived={archived}
                    busy={inlinePatchMut.isPending}
                    onCommit={(next) =>
                      inlinePatchMut.mutate({ localAssetsUrl: next })
                    }
                  />
                  {/* P2c Local Assets §2.1: browser flips between view
                      (read mode) and edit (content edit mode). Edit
                      mode wires onChange through a debounced inline
                      patch so a quick edit settles into one server
                      write rather than one per keystroke. */}
                  {row.localAssetsUrl && (
                    editing ? (
                      <DriveAssetsBrowser
                        mode="edit"
                        title="Local Assets"
                        folderUrl={row.localAssetsUrl}
                        value={effectiveLocalLabels}
                        onChange={handleLocalLabelsChange}
                        // Ad Preview Mockups Stage 1 §2.2: row-level Local
                        // Assets get the dropdown too, since per-spec they
                        // also surface in the public preview.
                        showAdType
                        // OrganicAssets §8.1: seed "Image/Video - Local - "
                        // default label text on new stubs.
                        labelKind="local"
                        // OrganicAssets §8 fix: shared stub-write handler
                        // (also wired on the view browser below) so default
                        // labels populate on any render, not only edit mode.
                        onStubsDetected={persistLocalStubs}
                        // Stage 1 §13.17. Row-scoped prune. No
                        // cascade — localAssetLabels are row-scoped
                        // by construction. DriveAssetsBrowser drops
                        // the orphan entries from form state via
                        // onChange after the server confirms, so
                        // the row's debounced inline-patch picks up
                        // the cleaned shape automatically.
                        pruneTarget={{
                          kind: 'row' as const,
                          rolloutId: rolloutId!,
                          rowId: rowId!,
                          rowLabel: locationLabel,
                          assetKind: 'local',
                        }}
                      />
                    ) : (
                      <DriveAssetsBrowser
                        mode="view"
                        title="Local Assets"
                        folderUrl={row.localAssetsUrl}
                        value={row.localAssetLabels ?? []}
                        showAdType
                        // OrganicAssets §8 fix: view-mode auto-create too.
                        labelKind="local"
                        onStubsDetected={persistLocalStubs}
                      />
                    )
                  )}
                </div>
              }
            />
            <Field
              label="Organic assets (per-location)"
              value={
                <div className="space-y-3">
                  <OrganicAssetsField
                    row={row}
                    archived={archived}
                    busy={inlinePatchMut.isPending}
                    onCommit={(next) =>
                      inlinePatchMut.mutate({ organicAssetsUrl: next })
                    }
                  />
                  {/* OrganicAssets §9.3: Organic mirror of the Local Assets
                      browser — edit mode wires the debounced label save +
                      stub-write (labelKind seeds "Image/Video - Organic - "),
                      view mode shows the snapshot with adType badges. */}
                  {row.organicAssetsUrl && (
                    editing ? (
                      <DriveAssetsBrowser
                        mode="edit"
                        title="Organic Assets"
                        folderUrl={row.organicAssetsUrl}
                        value={effectiveOrganicLabels}
                        onChange={handleOrganicLabelsChange}
                        showAdType
                        labelKind="organic"
                        onStubsDetected={persistOrganicStubs}
                        pruneTarget={{
                          kind: 'row' as const,
                          rolloutId: rolloutId!,
                          rowId: rowId!,
                          rowLabel: locationLabel,
                          assetKind: 'organic',
                        }}
                      />
                    ) : (
                      <DriveAssetsBrowser
                        mode="view"
                        title="Organic Assets"
                        folderUrl={row.organicAssetsUrl}
                        value={row.organicAssetLabels ?? []}
                        showAdType
                        // OrganicAssets §8 fix: view-mode auto-create too.
                        labelKind="organic"
                        onStubsDetected={persistOrganicStubs}
                      />
                    )
                  )}
                </div>
              }
            />
            <Field
              label="Google assets folder"
              value={
                editing && draft ? (
                  <input
                    type="text"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
                    value={draft.googleAssetsFolderUrl}
                    onChange={(e) => updateDraft('googleAssetsFolderUrl', e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/..."
                  />
                ) : row.googleAssetsFolderUrl ? (
                  <a
                    href={row.googleAssetsFolderUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {row.googleAssetsFolderUrl}
                  </a>
                ) : null
              }
              modified={diffSet.has('googleAssetsFolderUrl')}
            />
            <Field
              label="Other assets folder"
              value={
                editing && draft ? (
                  <input
                    type="text"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
                    value={draft.otherAssetsFolderUrl}
                    onChange={(e) => updateDraft('otherAssetsFolderUrl', e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/..."
                  />
                ) : row.otherAssetsFolderUrl ? (
                  <a
                    href={row.otherAssetsFolderUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {row.otherAssetsFolderUrl}
                  </a>
                ) : null
              }
              modified={diffSet.has('otherAssetsFolderUrl')}
            />
            {/* P2c polish round 1 (Fix 2): when not editing, the inline
                browser above renders the labels (read-only) inside the
                live-files grid, so the static label list here would be
                duplicate UI. Keep it ONLY in editing mode — Phase 1 has
                no row-level label override, so this stays as the
                snapshotted-from-Campaign list with the modification
                marker for diff awareness. */}
            {editing && (
              <Field
                label="Meta asset labels"
                value={
                  row.metaAssetLabels && row.metaAssetLabels.length > 0 ? (
                    <ul className="space-y-1">
                      {row.metaAssetLabels.map((a) => (
                        <li key={a.driveFileId} className="text-sm">
                          <span className="font-medium">{a.label || '(unlabelled)'}</span>{' '}
                          <span className="text-slate-500">— {a.driveFileName}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-slate-400 italic">(none)</span>
                  )
                }
                modified={diffSet.has('metaAssetLabels')}
              />
            )}
          </dl>
          {editing && (
            <p className="text-xs text-slate-500 mt-4">
              Local assets is a per-location field — edited above, not part of the
              Campaign content draft.
            </p>
          )}
        </SectionShell>

        <SectionShell id="re-history" title="History">
          <AuditTimeline
            entries={audit.data}
            isLoading={audit.isLoading}
            error={audit.error}
          />
        </SectionShell>
      </div>

      {refreshOpen && (
        <RefreshFromCampaignModal
          count={diffSet.size}
          busy={refreshMut.isPending}
          onCancel={() => {
            if (refreshMut.isPending) return;
            setRefreshOpen(false);
          }}
          onConfirm={() => refreshMut.mutate()}
        />
      )}

      {saveAsOpen && campaign && (
        <SaveAsCampaignModal
          brandAccountId={rollout.brandAccountId}
          brandName={
            // Use the rollout breadcrumb's resolved brand name when present;
            // fall back to the brand id so the modal still mounts cleanly
            // for unusual data states.
            rollout.brandAccountId
          }
          alternateCampaignName={campaign.name}
          busy={saveAsMut.isPending}
          error={saveAsError}
          onCancel={() => {
            if (saveAsMut.isPending) return;
            setSaveAsOpen(false);
            setSaveAsError(null);
          }}
          onConfirm={(args) => saveAsMut.mutate(args)}
        />
      )}

      {saveAsToast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 right-6 z-50 max-w-md rounded-md border border-slate-200 bg-white shadow-lg p-4 space-y-2"
        >
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm text-slate-900">
              {saveAsToast.activated ? (
                <>Campaign saved and activated.</>
              ) : (
                <>
                  Campaign saved as draft —{' '}
                  {saveAsToast.warnings.length} field
                  {saveAsToast.warnings.length === 1 ? '' : 's'} need more content.
                </>
              )}
            </p>
            <button
              type="button"
              onClick={() => setSaveAsToast(null)}
              aria-label="Dismiss"
              className="text-slate-400 hover:text-slate-700"
            >
              ×
            </button>
          </div>
          {!saveAsToast.activated && saveAsToast.warnings.length > 0 && (
            <ul className="text-xs text-slate-600 list-disc pl-5 space-y-0.5">
              {saveAsToast.warnings.slice(0, 5).map((w, i) => (
                <li key={i}>
                  <code className="font-mono text-[11px]">{w.path}</code>: {w.message}
                </li>
              ))}
              {saveAsToast.warnings.length > 5 && (
                <li className="italic text-slate-400">
                  +{saveAsToast.warnings.length - 5} more
                </li>
              )}
            </ul>
          )}
          <Link
            to={`/campaigns/${saveAsToast.campaignId}/edit`}
            className="text-xs text-blue-600 hover:underline inline-block"
          >
            {saveAsToast.activated
              ? 'Open the new Campaign →'
              : 'Open the Campaign to fix →'}
          </Link>
        </div>
      )}

      {drift && (
        <BudgetDriftModal
          platform={drift.platform}
          locationName={locationLabel}
          rolloutName={rollout.name}
          fromCents={drift.fromCents}
          toCents={drift.toCents}
          currency={account.currency}
          onPermanent={() => {
            budgetMut.mutate({
              platform: drift.platform,
              monthlyCents: drift.toCents,
              patchAccount: true,
            });
            setDrift(null);
          }}
          onOneTime={() => {
            budgetMut.mutate({
              platform: drift.platform,
              monthlyCents: drift.toCents,
              patchAccount: false,
            });
            setDrift(null);
          }}
          onCancel={() => setDrift(null)}
        />
      )}

      {/* Ad Preview Stage 3 §3.1: single-send modal + result toast. */}
      {sendOpen && (
        <SendPreviewModal
          rolloutId={rollout.id}
          rowId={row.id}
          locationAccountId={row.accountId}
          locationName={locationLabel}
          rowChannels={{ meta: metaActive, google: googleActive }}
          onClose={() => setSendOpen(false)}
          onSent={(result) => setSendResult(result)}
        />
      )}
      {sendResult && (
        <SendResultToast result={sendResult} onDismiss={() => setSendResult(null)} />
      )}
    </div>
  );
}
